import {
	AfterViewInit,
	computed,
	Component,
	DestroyRef,
	effect,
	ElementRef,
	HostListener,
	Injector,
	inject,
	input,
	InputSignal,
	OnDestroy,
	signal,
	untracked,
	ViewChild,
	WritableSignal,
	afterNextRender
} from '@angular/core';
import * as jsts from 'jsts';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SHARED_MODULES } from '../../../shared.';
import { SelectedPanelComponent } from '../selected-panel/selected-panel.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { ZoomComponent } from '../zoom/zoom.component';
import { Overlay } from '@angular/cdk/overlay';
import { TranslateService } from '@ngx-translate/core';
import { DndDropEvent, DndDropzoneDirective } from 'ngx-drag-drop';
import Konva from 'konva';
import { CanvasDrawerService } from '../../services/canvas-drawer.service';
import { DragOutsideService } from '../../services/drag-outside.service';
import { HighlightOptionsValue, HighlightProductsService } from '../../services/highlight-products.service';
import { HighlightLegendComponent } from '../../../planogram/components/highlight-legend/highlight-legend.component';
import { HotkeyTooltipComponent } from '../hotkey-tooltip/hotkey-tooltip.component';
import { PhTooltipDirective } from '../../directives/ph-tooltip.directive';
import { ServiceFeatures } from '../../models/serviceFeatures';
import { SnackBarService } from '../../services/snack-bar.service';
import { SessionService } from '../../services/session.service';
import { MeasurementUnitPipe } from '../../pipes/measurement-unit.pipe';
import { MeasurementUnitTranslatePipe } from '../../pipes/measurement-unit-translate.pipe';
import { Utils } from '../../services/utils.service';
import { Point } from '../../models/point';
import { Bounding } from '../../models/bounding';
import { Element } from '../../../equipment/models/elements/element';
import { elementsGenerator } from '../../../equipment/models/elements/elementGenerator';
import { EquipmentElements } from '../../../equipment/models/elements/equipmentElements';
import { ExtensionType } from '../../../equipment/models/elements/extension';
import { Rectangle } from '../../../equipment/models/figures/rectangle';
import { konvaSettings } from '../../../equipment/models/konvaSettings';
import { Block } from '../../../planogram/models/block';
import {
	areBlockElementTypesEqual,
	buildBlockElementsMismatchDetails,
	getBlockProductPlacementErrorKey
} from '../../../planogram/utils/block-move-errors.util';
import { HoleElement } from '../../../planogram/models/elements/holeElement';
import { HookElement } from '../../../planogram/models/elements/hookElement';
import { PalletElement } from '../../../planogram/models/elements/palletElement';
import { ShelfElement } from '../../../planogram/models/elements/shelfElement';
import { IPlanogramBounds } from '../../../planogram/planogram-edit/models/planogramBounds';
import { IPlanogram } from '../../../planogram/models/planogram';
import { ICanPutProductsResult } from '../../../planogram/models/canPutProductsResult';
import { ProductGroup } from '../../../planogram/models/product-group';
import { PlanogramViewBehavior } from '../../../planogram/models/planogramViewBehavior';
import { PlanogramViewBehaviorFactory } from '../../../planogram/models/planogramViewBehaviorFactory';
import { DetailedElementWidgetOverlayService } from '../../../planogram/services/detailed-element-widget-overlay.service';
import { CanvasUpdateRequest, PlanogramActivity } from '../../../planogram/services/planogram-activity.service';
import {
	ALL_PLANOGRAM_CANVAS_LAYERS,
	PlanogramCanvasLayer,
	resolvePlanogramCanvasLayers,
	shouldResizePlanogramCanvas
} from '../../../planogram/models/planogram-canvas-layer';
import { PlanogramModes } from '../../../planogram/services/planogram-modes.service';
import { PlanogramTooltipService } from '../../../planogram/services/planogram-tooltip.service';
import { Figure } from '../../../equipment/models/figures/figure';
import { UnitConverter } from '../../utils/unitConverter';
import { Side } from '../../models/side';
import { MixpanelService } from '../../services/mixpanel.service';
import { MixpanelEvents } from '../../models/mixpanelEvents';
import { GlobalSettings } from '../../models/globalSettings';
import { POSMaterial } from '../../../planogram/models/POSMaterials/POSMaterial';
import { POSImageMaterial, POSMaterialImageSaveObject } from '../../../planogram/models/POSMaterials/POSImageMaterial';
import { POSTextMaterial } from '../../../planogram/models/POSMaterials/POSTextMaterial';
import { POSTextVerticalAlign } from '../../../planogram/models/POSMaterials/posTextAlign';
import { EquipmentModelComponent } from "../equipment-model/equipment-model.component";
import { HasFeaturePipe } from '../../pipes/has-feature.pipe';
import { OverlayZIndexService } from '../../../planogram/services/overlay-z-index.service';
import { PhIconComponent } from "../ph-icon/ph-icon.component";
import { BlockDrawOverlay } from './block-draw-overlay';

const DRAGGABLE_ELEMENTS: { front: Array<EquipmentElements>; top: Array<EquipmentElements> } = {
	front: [
		EquipmentElements.Shelf,
		EquipmentElements.AngleShelf,
		EquipmentElements.HangBar,
		EquipmentElements.HookPanel,
		EquipmentElements.Hook,
		EquipmentElements.Perforation
	],
	top: [
		EquipmentElements.Box,
		EquipmentElements.SlicedBox,
		EquipmentElements.Partition
	]
};
const DETAILED_WIDGET_ELEMENTS = [ShelfElement, PalletElement, HookElement];
const BACK_WALLS_LIST: Array<EquipmentElements> = [
	EquipmentElements.BackWall,
	EquipmentElements.CentralPillar,
	EquipmentElements.HookWall,
	EquipmentElements.PerforatedWall,
	EquipmentElements.Showcase
];
const canvasOffset: number = konvaSettings.canvasOffset;

const JstsCoordinate = jsts.geom.Coordinate;
const geometryFactory = new (jsts.geom.GeometryFactory)();
const BLOCK_CHOICES = [0, 1];
const MIN_BLOCK_SIZE_METERS = 0.05;
const COMPACT_3D_MAX_WIDTH = 600;
const COMPACT_TOOLBAR_MAX_WIDTH = 400;
const COMPACT_SELECTED_PANEL_MAX_WIDTH = 300;
const CANVAS_RESIZE_DEBOUNCE_MS = 200;
const HIGHLIGHT_LEGEND: Array<HighlightOptionsValue> = [
	HighlightOptionsValue.Categories,
	HighlightOptionsValue.Brands,
	HighlightOptionsValue.Suppliers,
	HighlightOptionsValue.ActiveProducts,
	HighlightOptionsValue.InactiveProducts,
	HighlightOptionsValue.Status,
	HighlightOptionsValue.Format
];

const PRODUCT_LABELS_DICT = {
	PROMO: 'А',
	NEW: 'Н'
} as const;

interface ICanvasMultiSelectConfiguration {
	begin: Point | null;
	end: Point | null;
	drawing: boolean;
	ctrlKey: boolean;
}

@Component({
	selector: 'ph-planogram-canvas',
	templateUrl: './planogram-canvas.component.html',
	styleUrls: ['./planogram-canvas.component.scss'],
	imports: [
        ...SHARED_MODULES,
        DndDropzoneDirective,
        SelectedPanelComponent,
        PhIconWrapperComponent,
        ZoomComponent,
        EquipmentModelComponent,
        HighlightLegendComponent,
        HotkeyTooltipComponent,
        PhTooltipDirective,
        HasFeaturePipe,
        PhIconComponent
    ]
})
export class PlanogramCanvasComponent implements AfterViewInit, OnDestroy {

	planogram: InputSignal<IPlanogram> = input.required<IPlanogram>();
	overlayHost: InputSignal<HTMLElement> = input<HTMLElement>();
	editable: InputSignal<boolean> = input<boolean>(false);

    private _mainStage: Konva.Stage;
	private _planogramLayer: Konva.Layer = new Konva.Layer();
	private _blocksLayer: Konva.Layer = new Konva.Layer();
	private _posMaterialsLayer: Konva.Layer = new Konva.Layer();
	private _sizesLayer: Konva.Layer = new Konva.Layer();
    private _tooltipsLayer: Konva.Layer = new Konva.Layer();
	private _toolsLayer: Konva.Layer = new Konva.Layer();

    private readonly _blockResizeMode: WritableSignal<boolean> = signal(false);
    private readonly _blockResizeSide: WritableSignal<Side | null> = signal(null);
    private readonly _containerWidth: WritableSignal<number> = signal(0);
    private readonly _minBlockSize: number = UnitConverter.convertAndRoundToSaveValue(MIN_BLOCK_SIZE_METERS);
    private readonly _multiSelectConfiguration: ICanvasMultiSelectConfiguration = { begin: null, end: null, drawing: false, ctrlKey: false };
    private _resizeObserver: ResizeObserver;
	private readonly _resize$: Subject<void> = new Subject<void>();
    private _blockDrawOverlay: BlockDrawOverlay;
	readonly showRotateCursor: WritableSignal<boolean> = signal(false);
	private _posMaterialRotating: boolean = false;
	readonly zoom: WritableSignal<number> = signal(100);
	readonly model3DMode: WritableSignal<boolean> = signal(false);
	readonly posMaterialsMode: WritableSignal<boolean> = signal(true);
	readonly sizesMode: WritableSignal<boolean> = signal(false);
    readonly transparentBoxesMode: WritableSignal<boolean> = signal(false);
	readonly flipMode: WritableSignal<boolean> = signal(false);
	readonly imagesMode: WritableSignal<boolean> = signal(true);
    readonly sequenceNumberMode: WritableSignal<boolean> = signal(false);
    readonly productSortOptions: Array<{ name: string; by: string; type: 'asc' | 'desc' }>;
    readonly highlightSubject: Subject<void> = new Subject<void>();
    readonly highlightLegend: Array<HighlightOptionsValue> = HIGHLIGHT_LEGEND;

	@ViewChild('canvas', { static: true }) private _canvas: ElementRef<HTMLDivElement>;
	@ViewChild('canvasContainer') private _canvasContainer: ElementRef<HTMLDivElement>;
	@ViewChild('canvasScrollContainer') private _canvasScrollContainer: ElementRef<HTMLDivElement>;
	@ViewChild('canvasOverlayHost') private _canvasOverlayHost: ElementRef<HTMLDivElement>;
	@ViewChild('model3dContainer') private _model3dContainer: ElementRef<HTMLDivElement>;
	@ViewChild('equipment3DModel') private _equipment3DModel: EquipmentModelComponent;

    private readonly _host: ElementRef<HTMLElement> = inject(ElementRef);
	private readonly destroyRef: DestroyRef = inject(DestroyRef);
    private readonly canvasDrawerService: CanvasDrawerService = inject(CanvasDrawerService);
    private readonly highlightProductsService: HighlightProductsService = inject(HighlightProductsService);
    private readonly planogramTooltipService: PlanogramTooltipService = inject(PlanogramTooltipService);
    private readonly detailedElementWidgetOverlayService: DetailedElementWidgetOverlayService = inject(DetailedElementWidgetOverlayService);
	private readonly sessionService: SessionService = inject(SessionService);
	private readonly snackBarService: SnackBarService = inject(SnackBarService);
	private readonly mixPanelService: MixpanelService = inject(MixpanelService);
	private readonly translate: TranslateService = inject(TranslateService);
	private readonly measurementUnitPipe: MeasurementUnitPipe = inject(MeasurementUnitPipe);
	private readonly measurementUnitTranslatePipe: MeasurementUnitTranslatePipe = inject(MeasurementUnitTranslatePipe);
    private readonly overlay: Overlay = inject(Overlay);
	private readonly injector: Injector = inject(Injector);
    readonly features: typeof ServiceFeatures = ServiceFeatures;
	readonly modes: PlanogramModes = inject(PlanogramModes);
	readonly activity: PlanogramActivity = inject(PlanogramActivity);
	readonly dragOutsideService: DragOutsideService = inject(DragOutsideService);

    readonly flipIcon = computed((): string => this.flipMode() ? 'flipFront' : 'flipBack');
    readonly flipTooltip = computed((): string => this.flipMode() ? 'COMMON.FLIP_FRONT' : 'COMMON.FLIP_BACK');
	readonly flipEnabled = computed((): boolean => {
        const planogram: IPlanogram = this.planogram();
        if (!planogram) return false;
		return planogram.stand.allElements.some((el: Element) => el.parent.is(EquipmentElements.CentralPillar));
	});
	readonly hideCanvas = computed((): boolean =>
		this.model3DMode() && this._containerWidth() > 0 && this._containerWidth() < COMPACT_3D_MAX_WIDTH
	);
	readonly compactToolbar = computed((): boolean =>
		this._containerWidth() > 0 && this._containerWidth() < COMPACT_TOOLBAR_MAX_WIDTH
	);
	readonly compactSelectedPanel = computed((): boolean =>
		this._containerWidth() > 0 && this._containerWidth() < COMPACT_SELECTED_PANEL_MAX_WIDTH
	);
    readonly targetElement = computed(() => {
        const targetElement = this.activity.targetElement();
        if (!targetElement) return null;
        if (targetElement.stand === this.planogram().stand) return targetElement;
        return null;
    });
	readonly planogramActiveProducts = computed((): Array<ProductGroup> => {
		return this.activity.activeProducts().filter((pg: ProductGroup) => pg.element.stand === this.planogram().stand);
    });
	readonly planogramActiveBlocks = computed((): Array<Block> =>
		this.activity.activeBlocks().filter((block: Block) => block.stand === this.planogram().stand)
	);
	readonly planogramActivePosMaterials = computed((): Array<POSMaterial> =>
		this.activity.activePosMaterials().filter((material: POSMaterial) => this.planogram().stand.POSMaterials.includes(material))
	);
	readonly canChangePlanogramProductPosition = computed((): boolean =>
		this.activity.canChangeProductPositionFor(this.planogramActiveProducts())
	);
	readonly planogramSortingActive = computed((): boolean =>
		this.activity.isSortingActiveFor(this.planogram(), this.planogramActiveProducts(), this.planogramActiveBlocks())
	);
	readonly showElementDetailPanel = computed((): boolean => {
		const element: Element | null = this.targetElement();
		if (!element?.is(...DETAILED_WIDGET_ELEMENTS)) return false;
		return !this.planogram().stand.allElements.some((planogramElement: Element) =>
			this.detailedElementWidgetOverlayService.isOpened(planogramElement)
		);
	});
	readonly isContainerActive = computed((): boolean => {
        if (this.overlayHost()) return false;
		const canvas: HTMLDivElement = this._canvas?.nativeElement;
		if (!canvas || !this.activity.isCanvasFocused(canvas)) return false;
		return this.detailedElementWidgetOverlayService.hasOpenedForPlanogram(this.planogram());
	});

	constructor() {
		this.productSortOptions = [
			{ name: 'SIZE_ASC', by: 'Height', type: 'asc' },
			{ name: 'SIZE_DESC', by: 'Height', type: 'desc' }
		];
		if (this.sessionService.hasFeature(ServiceFeatures.ProductPrice)) {
			this.productSortOptions.push(
				{ name: 'PRICE_ASC', by: 'avgPrice', type: 'asc' },
				{ name: 'PRICE_DESC', by: 'avgPrice', type: 'desc' }
			);
		}

        this._resize$
            .pipe(debounceTime(CANVAS_RESIZE_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (!this.hideCanvas()) this.canvasUpdate();
                if (this.model3DMode()) this._equipment3DModel?.init();
            });

        effect(() => {
            this.zoom();
            untracked(() => {
                this.canvasUpdate();
                this._mainStage?.draw();
            });
        });

		effect(() => {
			this.sizesMode();
            untracked(() => this.renderSizesLayer());
		});

		effect(() => {
			if (this._canvas?.nativeElement) {
                this._canvas.nativeElement.style.cursor = this.modes.drawBlockMode() ? 'crosshair' : 'default';
            }
		});

		effect(() => {
			const imagesMode: boolean = this.modes.imagesMode();
			untracked(() => this.imagesMode.set(imagesMode));
		});

		effect(() => {
			const sequenceNumberMode: boolean = this.modes.sequenceNumberMode();
			untracked(() => this.sequenceNumberMode.set(sequenceNumberMode));
		});

		effect(() => {
			const transparentBoxesMode: boolean = this.modes.transparentBoxesMode();
			untracked(() => this.transparentBoxesMode.set(transparentBoxesMode));
		});

		effect(() => {
			const sizesMode: boolean = this.modes.sizesMode();
			untracked(() => this.sizesMode.set(sizesMode));
		});

		effect(() => {
			const flipMode: boolean = this.modes.flipMode();
			untracked(() => this.flipMode.set(flipMode));
		});

		effect(() => {
			this.activity.activeProducts();

			untracked(() => {
				if (!this._mainStage || this.hideCanvas()) return;
				this.planogram().stand.products.forEach((pg: ProductGroup) => {
					const canvasProduct: Konva.Group = this.canvasDrawerService.getCanvasProduct(pg);
					if (this.activity.isProductActive(pg)) {
						this.canvasDrawerService.setCanvasProductActive(pg, canvasProduct);
					} else {
						this.canvasDrawerService.setCanvasProductInactive(pg, canvasProduct);
					}
				});
			});
		});

		effect(() => {
			const activeBlock: Block = this.activity.activeBlocks()[0];

			untracked(() => {
				if (!this._mainStage || this.hideCanvas()) return;
                if (this.activity.productMove()) return;
				if (activeBlock) this.canvasDrawerService.getCanvasBlock(activeBlock).moveToTop();
				this.planogram().stand.blocks.forEach((block: Block) => {
					this.canvasDrawerService.changeBlockBordersStroke(block, activeBlock);
				});
			});
		});

		effect(() => {
			const selectedBlock: Block = this.activity.selectedBlock();

			untracked(() => {
				if (!this._mainStage || this.hideCanvas()) return;
                if (this.activity.productMove()) return;
				this.planogram().stand.blocks.forEach((block: Block) => {
					if (block == selectedBlock) {
						this.canvasDrawerService.getCanvasBlockBody(block).hide();
						this.canvasDrawerService.getCanvasBlockTitle(block).hide();
						this.canvasDrawerService.getCanvasBlockSizes(block).show();
					} else {
						this.canvasDrawerService.getCanvasBlockBody(block).show();
						this.canvasDrawerService.getCanvasBlockTitle(block).show();
						this.canvasDrawerService.getCanvasBlockSizes(block).hide();
					}
				});
			});
		});

		effect(() => {
			this.activity.activePosMaterials();

			untracked(() => {
				if (!this._mainStage) return;
				this.planogram().stand.POSMaterials.forEach((material: POSMaterial) => {
					if (this.activity.isPOSMaterialActive(material)) {
						this.canvasDrawerService.setCanvasPOSMaterialActive(material);
					} else {
						this.canvasDrawerService.setCanvasPOSMaterialInactive(material);
					}
				});
			});
		});

		effect(() => {
			const transparent: boolean = this.transparentBoxesMode();

			untracked(() => {
				if (!this._mainStage || this.hideCanvas()) return;
				if (transparent) {
					this._mainStage.find('.back-wall').forEach((shape: Konva.Shape) => shape.fill('transparent'));
					this._mainStage.find('.front-wall').forEach((shape: Konva.Shape) => shape.fill('transparent'));
				} else {
					this._mainStage.find('.back-wall').forEach((shape: Konva.Shape) => shape.fill(konvaSettings.fillGreyColor));
					this._mainStage.find('.front-wall').forEach((shape: Konva.Shape) => shape.fill(konvaSettings.fillWhiteColor));
				}
			});
		});

        effect(() => {
            this.sequenceNumberMode();
            this.imagesMode();
            this.activity.highlightValue();

            untracked(() => {
                if (!this._mainStage || this.hideCanvas()) return;
                this.renderPlanogramLayer();
            });
        });

        effect(() => {
            this.posMaterialsMode();
            this.imagesMode();

            untracked(() => {
                if (this._equipment3DModel && this.model3DMode()) {
                    this._equipment3DModel.init();
                }
            });
        });

        effect(() => {
            this.posMaterialsMode();

            untracked(() => {
                if (!this._mainStage || this.hideCanvas()) return;
                this.renderPOSMaterialsLayer();
            });
        });

		effect(() => {
			const drawBlockMode: boolean = this.modes.drawBlockMode();
			this.modes.equipmentEditMode();
			this.flipMode();

			untracked(() => {
				if (!drawBlockMode && this.blockDrawState.drawing) {
					this.cancelBlockDraw();
					this.stopProductMove();
				}

				if (!this._mainStage || this.hideCanvas()) return;
				this.canvasUpdate();
			});
		});

		effect(() => {
			const planogram: IPlanogram = this.planogram();

			untracked(() => {
				this.flipMode.set(!!planogram.flip);
			});
		});

		effect(() => {
			this.activity.highlightValue();
			this.activity.highlightedProducts();

			untracked(() => this.refreshHighlightLegend());
		});

		effect(() => {
			this.activity.labelsState();

			untracked(() => {
				if (!this._mainStage || this.hideCanvas() || this.editable()) return;
				this.renderPlanogramLayer();
				this.renderBlocksLayer();
			});
		});
	}

    ngOnInit(): void {
        this.dragOutsideService.drop$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((targetCanvas: HTMLElement) => {
				if (targetCanvas !== this._canvas.nativeElement) return;
				if (!this.activity.activeProducts().length) return;
				const planogram: IPlanogram = this.planogram();
				const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
					.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);
				this.handleProductGroupDrop(behavior, true);
			});

        this.dragOutsideService.posDrop$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((targetCanvas: HTMLElement) => {
				if (targetCanvas !== this._canvas.nativeElement) return;
				this.handlePOSMaterialOutsideDrop();
			});

        this.dragOutsideService.blockDrop$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((targetCanvas: HTMLElement) => {
				if (targetCanvas !== this._canvas.nativeElement) return;
				this.handleBlockOutsideDrop();
			});
    }

    ngAfterViewInit(): void {
		this._mainStage = new Konva.Stage({
			container: this._canvas.nativeElement
		});
		this._mainStage.add(this._planogramLayer);
		this._mainStage.add(this._blocksLayer);
		this._mainStage.add(this._posMaterialsLayer);
		this._mainStage.add(this._sizesLayer);
		this._mainStage.add(this._tooltipsLayer);
		this._mainStage.add(this._toolsLayer);
		this._planogramLayer.setZIndex(0);
		this._blocksLayer.setZIndex(1);
		this._posMaterialsLayer.setZIndex(2);
		this._sizesLayer.setZIndex(3);
		this._tooltipsLayer.setZIndex(4);
		this._toolsLayer.setZIndex(5);

		this._resizeObserver = new ResizeObserver((entries: Array<ResizeObserverEntry>) => {
            this._containerWidth.set(this._host.nativeElement.clientWidth);
			for (const entry of entries) {
				if (entry.target !== this._host.nativeElement) {
					this._resize$.next();
					break;
				}
			}
		});
		this._resizeObserver.observe(this._canvasContainer.nativeElement);
		this._resizeObserver.observe(this._host.nativeElement);
		this._containerWidth.set(this._host.nativeElement.clientWidth);

		this.initProductDragHandlers();
		this.initPOSDragHandlers();
		this.initCanvasUpdateHandler();
		this.initHighlightUpdateHandler();
		this.initPerforationNumbersHandler();
		this.initCanvasElementEvents();
		this.initStageEvents();
	}

	openDetailedElementWidget(element: Element): void {
		if (!element.is(...DETAILED_WIDGET_ELEMENTS)) return;
		if (this.modes.layoutRulesLocked()) return;

		if (this.detailedElementWidgetOverlayService.isOpened(element)) {
			this.detailedElementWidgetOverlayService.focus(element);
			return;
		}

		this.detailedElementWidgetOverlayService.open({
			overlay: this.overlay,
			parentInjector: this.injector,
			hostContainer: this.overlayHost() ?? this._canvasOverlayHost?.nativeElement,
			boundingRect: this._canvasScrollContainer.nativeElement.getBoundingClientRect(),
			element: element,
			planogram: this.planogram(),
			afterClose: () => {
				if (!this.activity.isCanvasFocused(this._canvas.nativeElement)) {
					this.activity.setFocusedCanvas(this.planogram().main_stand, null, this._canvas.nativeElement);
					this.planogram().stand.allElements.forEach((el: Element) => {
						el.shapes.forEach((shape: Figure) => ((shape as any).representation = 'front'));
					});
				}
			}
		});
	}



	private initCanvasElementEvents(): void {
		const el: HTMLDivElement = this._canvas.nativeElement;
		el.addEventListener('contextmenu', (event: MouseEvent) => {
			event.preventDefault();
		}, false);
	}

	private initStageEvents(): void {
		const stageContent = this._mainStage.getContent();

		this._mainStage.on('pointerdown', (event: Konva.KonvaEventObject<PointerEvent>) => {
			if (!this._mainStage) return;
			if (!this.editable()) return;
			if (this.modes.drawBlockMode() || this._blockResizeMode() || this.modes.equipmentEditMode() || this.activity.activeBlocks().length)
				return;

			const eventPointerPosition = this._mainStage.getPointerPosition(),
				pointedShape: any = this._mainStage.getIntersection(eventPointerPosition);

			if (pointedShape) {
				const shapeChain = [pointedShape, pointedShape.parent, pointedShape.parent?.parent];
				const isDraggable = shapeChain.some((shape) => shape?.draggable());
				const isProductSide = shapeChain.some((shape) => shape?.name() === 'product-side');

				if (isDraggable || isProductSide) return;
			}

			this._multiSelectConfiguration.begin = eventPointerPosition;
			this._multiSelectConfiguration.end = null;
			this._multiSelectConfiguration.drawing = true;
			this._multiSelectConfiguration.ctrlKey = event.evt[navigator.platform == 'MacIntel' ? 'metaKey' : 'ctrlKey'];

			if (stageContent?.setPointerCapture) stageContent.setPointerCapture(event.evt.pointerId);
		});

		this._mainStage.on('pointermove', (event: Konva.KonvaEventObject<PointerEvent>) => {
			if (!this._mainStage) return;
			event.evt.preventDefault();

			if (
				this._multiSelectConfiguration.begin &&
				!Utils.equalObjects(this._multiSelectConfiguration.begin, this._mainStage.getPointerPosition()) &&
				this._multiSelectConfiguration.drawing
			)
				this.activity.clearDragProducts();

			if (
				!this._multiSelectConfiguration.drawing ||
				!this._multiSelectConfiguration.begin ||
				this.modes.drawBlockMode() ||
				this.modes.equipmentEditMode() ||
				this.activity.dragProducts().length ||
				this.activity.activeBlocks().length
			)
				return;

			const eventPointerPosition = this._mainStage.getPointerPosition();
			const beginPoint = this._multiSelectConfiguration.begin;

			this._toolsLayer.destroyChildren();
			this._toolsLayer.add(new Konva.Rect({
				x: Math.min(eventPointerPosition.x, beginPoint.x),
				y: Math.min(eventPointerPosition.y, beginPoint.y),
				width: Math.abs(eventPointerPosition.x - beginPoint.x),
				height: Math.abs(eventPointerPosition.y - beginPoint.y),
				stroke: '#99ddff',
				strokeWidth: 1
			}));
			this._toolsLayer.draw();

			this._multiSelectConfiguration.end = eventPointerPosition;
		});

		this._mainStage.on('pointerup', (event: Konva.KonvaEventObject<PointerEvent>) => {
			if (stageContent.releasePointerCapture) stageContent.releasePointerCapture(event.evt.pointerId);

			this.onBlockDrawPointerUp(event);

			if (this._multiSelectConfiguration.drawing) {
				this._multiSelectConfiguration.drawing = false;
				this.renderToolsLayer();

				if (!this._multiSelectConfiguration.ctrlKey && this._multiSelectConfiguration.end) {
					this.activity.setProductActive(null, false, false);
				}

				if (!this._multiSelectConfiguration.begin || !this._multiSelectConfiguration.end) return;

				const planogram: IPlanogram = this.planogram();
				const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
					.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);
				const begin: Point = behavior.convertCanvasPointToPlanogram(this._multiSelectConfiguration.begin);
				const end: Point = behavior.convertCanvasPointToPlanogram(this._multiSelectConfiguration.end);
				const selectedArea = geometryFactory.createPolygon([
					new JstsCoordinate(begin.x, begin.y),
					new JstsCoordinate(begin.x, end.y),
					new JstsCoordinate(end.x, end.y),
					new JstsCoordinate(end.x, begin.y),
					new JstsCoordinate(begin.x, begin.y)
				]);
				const productsCoordinates = planogram.stand.products
					.filter((pg: ProductGroup) => planogram.stand.blocks.every((b) => !b.products.includes(pg)))
					.filter((pg: ProductGroup) => {
						const wall: Element = pg.element.findDepend(...BACK_WALLS_LIST);
						if (!wall) return true;
						if (this.flipMode()) {
							return pg.begin.z < wall.center.z;
						} else {
							return pg.begin.z >= wall.center.z;
						}
					})
					.map((pg: ProductGroup) => {
						const onAngleShelf = pg.element.parent.type == 'AngleShelf';
						const shelfHeight = onAngleShelf ? pg.element.Height : 0;
						const productBoundingX: Bounding = pg.getBoundingX();
						const productBoundingY: Bounding = pg.getBoundingY();

						if (this.flipMode()) {
							const standWidth: number = pg.element.stand.Width;

							return {
								coords: [
									{ x: standWidth - productBoundingX.min, y: productBoundingY.min - shelfHeight },
									{ x: standWidth - productBoundingX.max, y: productBoundingY.min - shelfHeight },
									{ x: standWidth - productBoundingX.max, y: productBoundingY.max - shelfHeight },
									{ x: standWidth - productBoundingX.min, y: productBoundingY.max - shelfHeight },
									{ x: standWidth - productBoundingX.min, y: productBoundingY.min - shelfHeight }
								],
								pg: pg
							};
						} else {
							return {
								coords: [
									{ x: productBoundingX.min, y: productBoundingY.min - shelfHeight },
									{ x: productBoundingX.max, y: productBoundingY.min - shelfHeight },
									{ x: productBoundingX.max, y: productBoundingY.max - shelfHeight },
									{ x: productBoundingX.min, y: productBoundingY.max - shelfHeight },
									{ x: productBoundingX.min, y: productBoundingY.min - shelfHeight }
								],
								pg: pg
							};
						}
					});
				const productsToAdd = productsCoordinates.filter(({ coords }) => {
					const productArea = geometryFactory.createPolygon(
						coords.map((p: Point) => new JstsCoordinate(p.x, p.y))
					);
					return selectedArea.intersects(productArea);
				});

				if (!productsToAdd.length) this.activity.setProductActive(null, false, false);
				productsToAdd.forEach((pg) => this.activity.setProductActive(pg.pg, true));

				this.activity.syncPanelFromCanvas();
				this.isNeedCloseRightPanel();
			}
		});
	}

	resetMultiSelect(): void {
		this._multiSelectConfiguration.begin = null;
		this._multiSelectConfiguration.end = null;
		this._multiSelectConfiguration.drawing = false;
	}

	private isNeedCloseRightPanel(): void {
		if (this.activity.panelType() == 'layoutrules') return;
		if (this.activity.panelType() == 'brand') return;
		if (this.activity.activeBlocks().length) return;
		if (this.activity.dragProducts().length) return;
		if (this.activity.settingsPanelDisabled()) this.activity.closePanel();
	}

	private canvasGetPoint(x: number, y: number): Point {
		const scale: number = this.planogram().scale;
		return { x: Utils.round(x / scale), y: Utils.round(y / scale) };
	}

	private static blockIntersects(activeBlock, allBlocks) {
		const activePoly = geometryFactory.createPolygon(
			activeBlock.coordinates.map((i) => new JstsCoordinate(i.x, i.y))
		);
		const allPolys = allBlocks.map((b) =>
			geometryFactory.createPolygon(b.coordinates.map((i) => new JstsCoordinate(i.x, i.y)))
		);
		return allPolys
			.filter((p) => activePoly.intersects(p) && !activePoly.touches(p))
			.map((p) => activePoly.intersection(p));
	}

	private canDrawBlock(copy, allBlocks: Block[] = []) {
		return PlanogramCanvasComponent.blockIntersects(copy, allBlocks).filter((poly) => !poly.isEmpty());
	}

	private canMoveBlock(b, copy): boolean {
		return areBlockElementTypesEqual(b, copy);
	}

	private refreshBlockElements(block: Block) {
		block.cache.elements = null;
		return block.elements;
	}

	// --- Block drawing ---

	private blockDrawState: { drawing: boolean; block: Block | null } = { drawing: false, block: null };
	private lastBlockDrawPointer: { layerX: number; layerY: number } | null = null;

	private getBlockDrawOverlay(): BlockDrawOverlay {
		if (!this._blockDrawOverlay) {
			this._blockDrawOverlay = new BlockDrawOverlay();
			this._blockDrawOverlay.attach(this._blocksLayer);
		}

		return this._blockDrawOverlay;
	}

	private hideBlockDrawOverlay(): void {
		this._blockDrawOverlay?.hide();
	}

	private cancelBlockDraw(): void {
		if (!this.blockDrawState.drawing && !(this._blockDrawOverlay?.previewRect.visible())) return;

		this.hideBlockDrawOverlay();
		this.blockDrawState = { drawing: false, block: null };
		this.lastBlockDrawPointer = null;
	}

	private isPointerOnCanvas(event: any): boolean {
		const canvas: HTMLDivElement | undefined = this._canvas?.nativeElement;
		if (!canvas) return false;

		const clientX: number | undefined = event.clientX ?? event.evt?.clientX;
		const clientY: number | undefined = event.clientY ?? event.evt?.clientY;

		if (clientX == null || clientY == null) return true;

		const rect: DOMRect = canvas.getBoundingClientRect();
		return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
	}

	onBlockCanvasMouseDown(event: any): void {
		if (!this.modes.drawBlockMode()) return;

		const planogram: IPlanogram = this.planogram();
		const x = Utils.round((event.layerX - canvasOffset - planogram.beginPoint.x) / planogram.scale);
		const y = Utils.round((event.layerY - canvasOffset - planogram.beginPoint.y) / planogram.scale);
		const z = 0;
		const clickedBlock = planogram.stand.blocks.find((b: Block) => b.isPointOn(x, planogram.stand.Height - y));

		if (clickedBlock) return;

		const block: Block = new Block({ begin: [{ x, y, z }] });
		block.setStand(planogram);
		block.depth = planogram.stand.Depth;

		this.activity.setProductMove(true);
		this.blockDrawState = { drawing: true, block };
		this.lastBlockDrawPointer = { layerX: event.layerX, layerY: event.layerY };
		const overlay: BlockDrawOverlay = this.getBlockDrawOverlay();
		overlay.previewRect.listening(false);
		overlay.previewRect.x(x + canvasOffset * 2 + planogram.beginPoint.x);
		overlay.previewRect.y(y);
	}

	onBlockCanvasMouseLeave(): void {
		if (!this.modes.drawBlockMode() || !this.blockDrawState.drawing || !this.lastBlockDrawPointer) return;
		this.onBlockDrawPointerUp(this.lastBlockDrawPointer);
	}

	onBlockDrawPointerMove(event: any): void {
		if (!this.modes.drawBlockMode()) return;

		const drawingBlock: Block = this.blockDrawState?.block;
		if (!this.blockDrawState.drawing || !drawingBlock) return;
		if (!this.isPointerOnCanvas(event)) return;

		this.lastBlockDrawPointer = { layerX: event.layerX, layerY: event.layerY };

		const planogram: IPlanogram = this.planogram();
		const { scale, beginPoint } = planogram;
		const x = event.layerX - beginPoint.x;
		const y = event.layerY - canvasOffset - beginPoint.y;
		const z = 0;
		const point = this.canvasGetPoint(x, y);
		const reverseDrawX = point.x < drawingBlock.begin[0].x;
		const reverseDrawY = point.y < drawingBlock.begin[0].y;
		const rectCoord = Utils.reverseBlockDrawCoordinates(drawingBlock.begin[0], point);
		const pointsStart = { x: rectCoord.x1, y: rectCoord.y1, z };
		const width = Utils.round(rectCoord.x2 - rectCoord.x1, 2);
		const height = rectCoord.y2 - rectCoord.y1;
		const drawSideX = drawingBlock.begin[0].x - pointsStart.x;
		const drawSideY = drawingBlock.begin[0].y - pointsStart.y;
		const overlay: BlockDrawOverlay = this.getBlockDrawOverlay();

		overlay.previewRect.fill('#ff5a00');
		overlay.previewRect.opacity(0.4);
		overlay.previewRect.x(drawSideX == 0 ? rectCoord.x1 * scale + canvasOffset + beginPoint.x : rectCoord.x1 * scale + beginPoint.x);
		overlay.previewRect.y(rectCoord.y1 * scale + beginPoint.y + canvasOffset);
		overlay.previewRect.width(drawSideX == 0 ? width * scale - canvasOffset : width * scale + canvasOffset);
		overlay.previewRect.height(height * scale);

		drawingBlock.clearCache();
		drawingBlock.width = reverseDrawX ? width + canvasOffset / scale : width - canvasOffset / scale;
		drawingBlock.height = height;

		if (reverseDrawX || reverseDrawY) {
			drawingBlock.updateCoordinatesForReverseDrawBlock(reverseDrawX, reverseDrawY);
		}
		drawingBlock.cache.elements = this.refreshBlockElements(drawingBlock);

		overlay.raiseLabels(this._blocksLayer);

		overlay.widthLabel.show();
		overlay.widthLabel.getTag().fill(drawingBlock.image ? '#FFFFFF' : '');
		overlay.widthLabel.getText().text(
			UnitConverter.getConvertValue(
				Math.abs(drawSideX == 0 ? width - canvasOffset / scale : width + canvasOffset / scale),
				'distance', 'inches', 3
			).toString()
		);
		overlay.widthLabel.setAttrs({
			x: drawSideX == 0 ? event.layerX - (width * scale) / 2 : event.layerX + (width * scale) / 2,
			y: drawSideY == 0 ? event.layerY - height * scale : event.layerY + height * scale - canvasOffset / 2
		});

		overlay.heightLabel.show();
		overlay.heightLabel.getTag().fill(drawingBlock.image ? '#FFFFFF' : '');
		overlay.heightLabel.getText().text(Utils.round(height, 2).toString());
		overlay.heightLabel.setAttrs({
			x: drawSideX == 0 ? event.layerX - overlay.heightLabel.height() : event.layerX + overlay.heightLabel.height(),
			y: drawSideY == 0
				? event.layerY - (height * scale) / 2 + overlay.heightLabel.width() / 2
				: event.layerY + (height * scale) / 2 + overlay.heightLabel.width() / 2
		});

		overlay.percentLabel.show();
		overlay.percentLabel.getTag().fill(drawingBlock.image ? '#FFFFFF' : '');
		overlay.percentLabel.getText().text(drawingBlock.percent + '%');
		overlay.percentLabel.setAttrs({
			x: drawSideX == 0 ? event.layerX - width * scale + canvasOffset : event.layerX,
			y: drawSideY == 0 ? event.layerY - height * scale : event.layerY
		});

		overlay.previewRect.visible(true);
		this._blocksLayer.draw();
	}

	onBlockDrawPointerUp(event: any): void {
		if (!this.modes.drawBlockMode()) {
			if (this.blockDrawState.drawing) {
				this.cancelBlockDraw();
				this.stopProductMove();
			}
			return;
		}

		const drawingBlock: Block = this.blockDrawState.block;
		if (!this.blockDrawState.drawing || !drawingBlock) return;

		this.hideBlockDrawOverlay();

		const planogram: IPlanogram = this.planogram();
		const x = (event.evt?.layerX ?? event.layerX) - canvasOffset - planogram.beginPoint.x;
		const y = (event.evt?.layerY ?? event.layerY) - canvasOffset - planogram.beginPoint.y;
		const scaledX = x / planogram.scale;
		const scaledY = y / planogram.scale;
		const z = 0;
		const rectCoord = Utils.reverseBlockDrawCoordinates(drawingBlock.begin[0], { x: scaledX, y: scaledY });
		const width = UnitConverter.convertAndRoundToSaveValue(rectCoord.x2 - rectCoord.x1, 'distance', 'inches', 3);
		const height = UnitConverter.convertAndRoundToSaveValue(rectCoord.y2 - rectCoord.y1, 'distance', 'inches', 3);
		const pointsStart = { x: rectCoord.x1, y: rectCoord.y1, z };

		if (this.flipMode()) pointsStart.x = Utils.round(planogram.stand.Width - (pointsStart.x + width));

		drawingBlock.updateCoordinates([pointsStart], width, height);
		drawingBlock.depth = planogram.stand.Depth;
		drawingBlock.setBlockColor();
		drawingBlock.name = Block.setBlockTitle(
			planogram.stand.blocks.filter((b: Block) => b !== drawingBlock).map((b: Block) => b.name)
		);

		const otherBlocks: Array<Block> = planogram.stand.blocks.filter((b: Block) => b !== drawingBlock);
		const intersections = this.canDrawBlock(drawingBlock, otherBlocks);

		if (intersections.length) {
			this.snackBarService.setSnackBarNotification({ messageText: 'BLOCK_OVER_BLOCKS_ERROR' });
		}

		const drawingBlockIndex: number = planogram.stand.blocks.indexOf(drawingBlock);

		if (intersections.length || width <= this._minBlockSize || height <= this._minBlockSize) {
			if (drawingBlockIndex !== -1) planogram.stand.blocks.splice(drawingBlockIndex, 1);
			this.cancelBlockDraw();
			this.stopProductMove();
			this._blocksLayer.draw();
			return;
		}

		drawingBlock.setProducts(Utils.to1DArray(planogram.stand.blocks.map((b: Block) => b.products)));
		planogram.stand.blocks.push(drawingBlock);
		this.cancelBlockDraw();

		this.activity.planogramUpdate$.next(planogram);
		this.activity.setBlockActive(drawingBlock);
		this.activity.historyGenerate$.next();
		this.activity.requestCanvasUpdate([planogram]).subscribe({
			complete: () => this.stopProductMove()
		});
	}

	ngOnDestroy(): void {
		this._resizeObserver?.disconnect();
		this._mainStage?.destroy();
	}

	toggle3DModel(): void {
		this.model3DMode.update((value: boolean) => !value);

		const container: HTMLDivElement | undefined = this._model3dContainer?.nativeElement;

		if (!this.model3DMode()) {
			if (container) this._resizeObserver?.unobserve(container);
			return;
		}

		afterNextRender(() => {
			const model3dContainer: HTMLDivElement | undefined = this._model3dContainer?.nativeElement;

			if (!this.model3DMode() || !model3dContainer) return;

			this._resizeObserver.observe(model3dContainer);
		}, { injector: this.injector });
	}

	toggleSizesMode(): void {
		this.sizesMode.update((value: boolean) => !value);
	}

	toggleFlipMode(): void {
		if (!this.flipEnabled()) return;

		this.flipMode.update((value: boolean) => !value);
		this.planogram().flip = this.flipMode();
	}

	toggleShowPOSMaterials(): void {
		this.posMaterialsMode.update((value: boolean) => !value);
	}

	toggleImagesMode(): void {
		this.imagesMode.update((value: boolean) => !value);
	}

	toggleSequenceNumberMode(): void {
		this.sequenceNumberMode.update((value: boolean) => !value);
	}

	@HostListener('window:focus') onTabOpen(): void {
		this._mainStage?.batchDraw();
	}

	@HostListener('mousedown', ['$event']) onContainerMouseDown(event: MouseEvent): void {
		if ((event.target as HTMLElement).closest('detailed-element-widget')) return;

		const planogram: IPlanogram = this.planogram();
		if (!planogram || this.activity.isCanvasFocused(this._canvas.nativeElement)) return;

		const previousMainStand: number | null = this.activity.focusedCanvas().planogram;

		if (
			this.modes.equipmentEditMode()
			&& previousMainStand != null
			&& previousMainStand !== planogram.main_stand
		) {
			const previousPlanogram: IPlanogram | undefined = this.activity.allPlanograms()
				.find((item: IPlanogram) => item.main_stand === previousMainStand);

			if (previousPlanogram && previousPlanogram.stand.id === planogram.stand.id) {
				this.activity.syncEquipmentStandFromSource(previousPlanogram, planogram);
			}
		}

		this.activity.setFocusedCanvas(planogram.main_stand, null, this._canvas.nativeElement);
		planogram.stand.allElements.forEach((el: Element) => {
			el.shapes.forEach((shape: Figure) => ((shape as any).representation = 'front'));
		});

		if (this.modes.equipmentEditMode()) {
			this.activity.clearEquipmentDistanceLines();
		}
	}

	@HostListener('window:mousemove', ['$event']) onWindowMouseMove(event: MouseEvent): void {
		this.onBlockDrawPointerMove(event);
	}

	@HostListener('window:touchmove', ['$event']) onWindowTouchMove(event: TouchEvent): void {
		this.onBlockDrawPointerMove(event.changedTouches[0] as any);
	}

	@HostListener('window:touchend', ['$event']) onWindowTouchEnd(event: TouchEvent): void {
		this.onBlockDrawPointerUp(event.changedTouches[0] as any);
	}

	refreshHighlightLegend(force: boolean = false): void {
		if (force || this.highlightLegend.includes(this.activity.highlightValue())) {
			this.highlightSubject.next();
		}
	}

	onCanvasDrop(event: DndDropEvent): void {
		this.activity.canvasDrop$.next({ planogram: this.planogram(), event });
	}

	onDeleteItemsRequest(): void {
		void this.activity.deleteItems(this.planogram());
	}

	onCopyItemsRequest(): void {
		this.activity.copyPOSMaterial(this.planogram());
	}

	onSortProducts(option: { name: string; by: string; type: 'asc' | 'desc' }): void {
		this.activity.sortSelectedProductGroups(this.planogram(), option);
	}

	zoomApply(value: number): void {
		this.zoom.set(value);
	}

	getCanvasImage(): string {
		if (!this._mainStage || this.hideCanvas()) return '';

		this._mainStage.batchDraw();

		return this._mainStage.toDataURL({ pixelRatio: window.devicePixelRatio || 1 });
	}

	changeBoxesTransparentMode(): void {
		this.transparentBoxesMode.update((value: boolean) => !value);

		if (this.transparentBoxesMode()) {
			this.mixPanelService.trackPage(MixpanelEvents.TransparentBoxes, { page: 'planogram edit' });
		}
	}

	canvasUpdate(elements: Array<string> = [], layers?: Array<PlanogramCanvasLayer>): void {
		if (!this.planogram() || !this._canvasContainer?.nativeElement || !this._mainStage || this.hideCanvas()) return;

		const layersToRender: Array<PlanogramCanvasLayer> = resolvePlanogramCanvasLayers(layers);
		let paintElements: Array<string> = elements;
		const resized: boolean = shouldResizePlanogramCanvas(layers) && this.canvasResize();
		if (resized) paintElements = [];

		const finalLayers: Array<PlanogramCanvasLayer> = resized
			? [...ALL_PLANOGRAM_CANVAS_LAYERS]
			: layersToRender;

		this.canvasRender(paintElements, finalLayers);
	}

	private finalizeEquipmentMove(planogram: IPlanogram): void {
		this.activity.finalizeSharedEquipmentMove(planogram);
	}

	canvasResize(): boolean {
		const planogram: IPlanogram = this.planogram();
		const zoom: number = this.zoom();
		const containerWidth: number = this._canvasContainer.nativeElement.clientWidth - 1;
		const containerHeight: number = this._canvasContainer.nativeElement.clientHeight - 1;
		const prevScale: number = planogram.scale;
		const prevWidth: number = planogram.width;
		const prevHeight: number = planogram.height;
		const prevBeginPointX: number = planogram.beginPoint?.x ?? 0;
		const prevBeginPointY: number = planogram.beginPoint?.y ?? 0;
		const hasThinBase: boolean = planogram.stand.bases
			.some((base: Element) => Utils.round(base.Height) <= 0.01);
		const bottomDistanceForLabels: number = hasThinBase ? 0.12 : 0;
		const standBounds: IPlanogramBounds = Utils.calculatePlanogramBounds(planogram.stand);
		const instrumentWidth: number = standBounds.width;
		const instrumentHeight: number = Utils.round(standBounds.height + bottomDistanceForLabels);

		if (instrumentWidth / instrumentHeight > containerWidth / containerHeight) {
			planogram.scale = Math.floor(((containerWidth - canvasOffset * 2) / instrumentWidth) * (zoom / 100));
		} else {
			planogram.scale = Math.floor(((containerHeight - canvasOffset * 2) / instrumentHeight) * (zoom / 100));
		}

		planogram.width = planogram.scale * instrumentWidth;
		planogram.height = planogram.scale * instrumentHeight;

		const offsetX: number = standBounds.minX < 0 ? Math.abs(standBounds.minX) * planogram.scale : 0;
		const offsetY: number = (standBounds.maxY - planogram.stand.Height) * planogram.scale;
		planogram.beginPoint = { x: offsetX, y: offsetY };

		const width: number = planogram.width + canvasOffset * 2;
		const height: number = planogram.height + canvasOffset * 2;

		if (containerHeight > height) {
			planogram.beginPoint.y += Math.ceil((containerHeight - height) / 2);
		}
		if (planogram.height < height - canvasOffset * 2) {
			planogram.beginPoint.y += height - canvasOffset * 2 - planogram.height;
		}
		planogram.beginPoint.y = Math.round(planogram.beginPoint.y);

		if (containerWidth > width) {
			planogram.beginPoint.x = offsetX + Math.ceil((containerWidth - width) / 2);
		} else {
			planogram.beginPoint.x = offsetX;
		}
		planogram.beginPoint.x = Math.round(planogram.beginPoint.x);

		const finalWidth: number = zoom === 100 ? containerWidth : containerWidth > width ? containerWidth : width;
		const finalHeight: number = containerHeight > height ? containerHeight : height;

		this._canvas.nativeElement.style.width = `${finalWidth}px`;
		this._canvas.nativeElement.style.height = `${finalHeight}px`;

		this._mainStage.width(finalWidth);
		this._mainStage.height(finalHeight);

		return planogram.scale !== prevScale
			|| planogram.width !== prevWidth
			|| planogram.height !== prevHeight
			|| planogram.beginPoint.x !== prevBeginPointX
			|| planogram.beginPoint.y !== prevBeginPointY;
	}

	canvasRender(elements: Array<string> = [], layers: Array<PlanogramCanvasLayer> = [...ALL_PLANOGRAM_CANVAS_LAYERS]): void {
		const layerSet: Set<PlanogramCanvasLayer> = new Set(layers);

		if (layerSet.has(PlanogramCanvasLayer.Planogram)) {
			this.renderPlanogramLayer(elements);
		}
		if (layerSet.has(PlanogramCanvasLayer.Blocks)) {
			this.renderBlocksLayer(elements);
		}
		if (layerSet.has(PlanogramCanvasLayer.PosMaterials)) {
			this.renderPOSMaterialsLayer();
		}
		if (layerSet.has(PlanogramCanvasLayer.Sizes)) {
			this.renderSizesLayer(elements);
		}
		if (layerSet.has(PlanogramCanvasLayer.Tooltips)) {
			this.renderTooltipsLayer(elements);
		}
		if (layerSet.has(PlanogramCanvasLayer.Tools)) {
			this.renderToolsLayer(elements);
		}
	}

	renderEquipment(elements: Array<string> = []): void {
		this.renderPlanogramLayer(elements);
		this.renderTooltipsLayer(elements);
		this.renderToolsLayer(elements);
	}

	renderPlanogramLayer(elements: Array<string> = []): void {
		const resized: boolean = this.canvasResize();
		this.paintPlanogramLayer(resized ? [] : elements);
	}

	private paintPlanogramLayer(elements: Array<string> = []): void {
        if (!this._mainStage) return;
		const planogram: IPlanogram = this.planogram();
		this.clearPlanogramLayer(elements);
		this.forEachEquipmentElement(elements, (element: Element) => this.drawPlanogramElement(element));
		this.drawProducts(elements);
		this.canvasDrawerService.setCorrectPlanogramOrder(planogram.stand, this.flipMode());
		this._planogramLayer.batchDraw();
	}

	renderBlocksLayer(elements: Array<string> = []): void {
		this.clearBlocksLayer(elements);

		if (!this.editable() && !this.activity.labelsState().blocks) {
			this._blocksLayer.batchDraw();
			return;
		}

		const planogram: IPlanogram = this.planogram();
		const beginPoint: Point = planogram.beginPoint;
		const scale: number = planogram.scale;

		planogram.stand.blocks.forEach((b: Block) => {
			b.cache.elements = null;
			b.setProducts(
				Utils.to1DArray(planogram.stand.blocks.filter((block) => block != b).map((block) => block.products))
			);
			const canvasBlock: Konva.Group = this.canvasDrawerService.createCanvasBlock({
				block: b,
				beginPoint: planogram.beginPoint,
				scale: planogram.scale,
				active: b == this.activity.activeBlocks()[0],
				selected: b == this.activity.selectedBlock(),
				draggable: !this.modes.drawBlockMode() && !this.modes.equipmentEditMode() && this.editable(),
				zoom: this.zoom(),
				flip: this.flipMode()
			});

			if (!this.modes.drawBlockMode() && !this.modes.equipmentEditMode()) {
				const blockRect: Konva.Rect = this.canvasDrawerService.getCanvasBlockBody(b),
					blockDescriptionIcon: Konva.Image = this.canvasDrawerService.getCanvasBlockDescriptionIcon(b),
					blockName: Konva.Text = this.canvasDrawerService.getCanvasBlockName(b),
					sizes: Konva.Group = this.canvasDrawerService.getCanvasBlockSizes(b),
					sizesCursor = { top: 'n-resize', right: 'e-resize', bottom: 's-resize', left: 'w-resize' },
					productsPositions: Map<ProductGroup, Konva.Vector2d> = b.products
						.reduce((result: Map<ProductGroup, Konva.Vector2d>, pg: ProductGroup) => {
							result.set(pg, this.canvasDrawerService.getCanvasProduct(pg).position());
							return result;
						}, new Map<ProductGroup, Konva.Vector2d>());

				blockRect.on('click', () => {
					if (!this.editable()) return;
					this.activity.clearSelectedBlock();
					this.activity.setBlockActive(b);
				});

				blockRect.on('dblclick', () => {
					if (!this.editable()) return;
					this.activity.setBlockActive();
					this.activity.setSelectedBlock(b);
				});

				blockRect.on('dragstart', () => {
					this.resetMultiSelect();

					this.activity.setProductMove(true);
					this.activity.clearSelectedBlock();
					if (!this.activity.isBlockActive(b)) this.activity.setBlockActive(b);
					this.clearBlockDragOutsidePreviews();

					this.canvasDrawerService.getCanvasBlockBorders(b).hide();
					this.canvasDrawerService.getCanvasBlockSizes(b).hide();
					this.canvasDrawerService.getCanvasBlockTitle(b).hide();
					b.updateBlockProducts(this.activity.getDependBlockProducts(b));
					b.products
						.sort((a, b) => a.begin.z - b.begin.z)
						.forEach((p) => {
							this.canvasDrawerService.getCanvasProduct(p).moveToTop();
						});
					this._blocksLayer.draw();
				});

				blockRect.on('dragmove', () => {
					b.products.forEach((p) => {
						const canvasProduct = this.canvasDrawerService.getCanvasProduct(p);
						canvasProduct.x(productsPositions.get(p).x + blockRect.x());
						canvasProduct.y(productsPositions.get(p).y + blockRect.y());
					});

					this._blocksLayer.draw();
					this._planogramLayer.draw();
					this.updateBlockDragOutsidePreviews(b);
				});

				blockRect.on('dragend', () => {
					this.handleBlockDragEnd(b, planogram, canvasBlock, blockRect);
				});

				if (this.canvasDrawerService.getCanvasBlockTooltipNecessity(b)) {
					const tooltip = this.canvasDrawerService.createCanvasBlockTooltip(b, b.name, this._canvas.nativeElement);
					this._tooltipsLayer.add(tooltip);
					blockName.listening(true);

					blockName.on('mouseenter', () => {
						tooltip.show();
						this._tooltipsLayer.draw();
					});

					blockName.on('mouseleave', () => {
						tooltip.hide();
						this._tooltipsLayer.draw();
					});
				}

				if (b.description) {
					const descriptionTooltip = this.canvasDrawerService.createCanvasBlockDescriptionTooltip(
						b,
						b.description,
						this._canvas.nativeElement
					);
					this._tooltipsLayer.add(descriptionTooltip);
					blockDescriptionIcon.listening(true);

					blockDescriptionIcon.on('mouseenter', () => {
						descriptionTooltip.show();
						this._tooltipsLayer.draw();
					});

					blockDescriptionIcon.on('mouseleave', () => {
						descriptionTooltip.hide();
						this._tooltipsLayer.draw();
					});
				}

				sizes.find('.size-area').forEach((node: Konva.Rect) => {
					node.on('mousedown', () => {
						this._blockResizeMode.set(true);
					});

					node.on('mouseup', () => {
						this._blockResizeMode.set(false);
					});

					node.on('dragstart', () => {
						this.activity.setProductMove(true);
						this._blockResizeSide.set(node.id() as Side);

						blockRect.show();
						this.canvasDrawerService.getCanvasBlockBorders(b).hide();
						this.canvasDrawerService.getCanvasBlockSizes(b).hide();
						this.canvasDrawerService.getCanvasBlockTitle(b).hide();
					});

					node.on('dragmove', () => {
						let height: number, width: number;
						const blockCopy: Block = b.copy();
						blockCopy.clearCache();
						const overlay: BlockDrawOverlay = this.getBlockDrawOverlay();

						overlay.raiseLabels(this._blocksLayer);
						overlay.widthLabel.show();
						overlay.heightLabel.show();
						overlay.percentLabel.show();

						switch (node.id()) {
							case 'bottom':
								height = UnitConverter.convertAndRoundToSaveValue(
									(node.y() + node.height() / 2) / scale, 'distance', 'inches', 3
								);
								if (height < this._minBlockSize) height = this._minBlockSize;
								blockRect.height(height * scale);
								blockCopy.height = height;
								break;

							case 'top':
								height = UnitConverter.convertAndRoundToSaveValue(
									(canvasBlock.height() - node.y() - node.height() / 2) / scale, 'distance', 'inches', 3
								);
								if (height < this._minBlockSize) height = this._minBlockSize;
								blockRect.height(height * scale);
								blockCopy.height = height;
								blockRect.y(Utils.round(0 - (blockRect.height() - canvasBlock.height())));
								blockCopy.begin[0].y = Utils.round((canvasBlock.y() + blockRect.y() - beginPoint.y - canvasOffset) / scale);
								break;

							case 'right':
								width = UnitConverter.convertAndRoundToSaveValue(
									(node.x() + node.width() / 2) / scale, 'distance', 'inches', 3
								);
								if (width < this._minBlockSize) width = this._minBlockSize;
								blockRect.width(width * scale);
								blockCopy.width = width;
								break;

							case 'left':
								width = UnitConverter.convertAndRoundToSaveValue(
									(canvasBlock.width() - node.x() - node.width() / 2) / scale, 'distance', 'inches', 3
								);
								if (width < this._minBlockSize) width = this._minBlockSize;
								blockRect.width(width * scale);
								blockCopy.width = width;
								blockRect.x(0 - (blockRect.width() - canvasBlock.width()));
								blockCopy.begin[0].x = Utils.round((canvasBlock.x() + blockRect.x() - beginPoint.x - canvasOffset) / scale);
								break;
						}

						if (blockCopy.image) {
							blockRect.fillPatternScale({
								x: blockRect.width() / blockRect.fillPatternImage().width,
								y: blockRect.height() / blockRect.fillPatternImage().height
							});
						}

						blockCopy.cache.elements = this.refreshBlockElements(blockCopy);
						overlay.heightLabel.getTag().fill(blockCopy.image ? '#FFFFFF' : '');
						overlay.heightLabel.getText().text(
							UnitConverter.getConvertValue(
								Math.abs(blockRect.height() / scale), 'distance', 'inches', 3
							).toString());
						overlay.widthLabel.getTag().fill(blockCopy.image ? '#FFFFFF' : '');
						overlay.widthLabel.getText().text(
							UnitConverter.getConvertValue(
								Math.abs(blockRect.width() / scale), 'distance', 'inches', 3
							).toString());
						overlay.percentLabel.getTag().fill(blockCopy.image ? '#FFFFFF' : '');
						overlay.percentLabel.getText().text(blockCopy.percent + '%');
						overlay.heightLabel.setAttrs({
							x: canvasBlock.x() + blockRect.x() + overlay.heightLabel.height(),
							y: canvasBlock.y() + blockRect.y() + blockRect.height() / 2 + overlay.heightLabel.width() / 2
						});
						overlay.widthLabel.setAttrs({
							x: canvasBlock.x() + blockRect.x() + blockRect.width() / 2 - overlay.widthLabel.width() / 2,
							y: canvasBlock.y() + blockRect.y() + blockRect.height() - overlay.widthLabel.height()
						});
						overlay.percentLabel.setAttrs({
							x: canvasBlock.x() + blockRect.x(),
							y: canvasBlock.y() + blockRect.y()
						});

						this._blocksLayer.draw();
					});

					node.on('dragend', () => {
						const resizeSide: Side = this._blockResizeSide();

						if (!this.activity.selectedBlock() && !this.activity.activeBlocks().length) {
							node.off('dragend');
							this._canvas.nativeElement.style.cursor = 'default';
							this.hideBlockDrawOverlay();
							this._blockResizeMode.set(false);
							this._blockResizeSide.set(null);
							this.stopProductMove();
							return;
						}

						this._blockResizeMode.set(false);
						this._blockResizeSide.set(null);
						if (this.activity.selectedBlock() == b) blockRect.hide();
						this.canvasDrawerService.getCanvasBlockBorders(b).show();
						this.canvasDrawerService.getCanvasBlockSizes(b).show();
						this.canvasDrawerService.getCanvasBlockTitle(b).show();

						this.hideBlockDrawOverlay();

						let x: number = canvasBlock.x() + blockRect.x() - canvasOffset - beginPoint.x,
							y: number = canvasBlock.y() + blockRect.y() - canvasOffset - beginPoint.y;

						if (resizeSide == 'right') x += blockRect.width();
						if (resizeSide == 'bottom') y += blockRect.height();

						const point: Point = this.canvasGetPoint(x, y),
							elements: Array<Element> = b.elements,
							copy: Block = b.copy();

						let finalResizeSide: Side | null = resizeSide;

						if (this.flipMode()) {
							if (resizeSide == 'right') finalResizeSide = 'left';
							if (resizeSide == 'left') finalResizeSide = 'right';
							point.x = b.stand.Width - point.x;
						}

						copy.resize(point.x, point.y, finalResizeSide);

						const intersections = this.canDrawBlock(copy, planogram.stand.blocks.filter((bl) => bl != b));

						if (intersections.length) {
							switch (finalResizeSide) {
								case 'left':
									point.x = Math.max.apply(null, intersections.map((p) => p.getCoordinates().map((c) => c.x)).flat());
									break;
								case 'right':
									point.x = Math.min.apply(null, intersections.map((p) => p.getCoordinates().map((c) => c.x)).flat());
									break;
								case 'top':
									point.y = Math.max.apply(null, intersections.map((p) => p.getCoordinates().map((c) => c.y)).flat());
									break;
								case 'bottom':
									point.y = Math.min.apply(null, intersections.map((p) => p.getCoordinates().map((c) => c.y)).flat());
									break;
							}
						}

						b.resize(point.x, point.y, finalResizeSide);

						this.activity.normalizeElements(planogram.stand, [...elements, ...b.elements]);
						const blockProductErrors = copy.setProducts();

						if (blockProductErrors.length) this.activity.showDeleteMessage$.next({ block: b, errors: blockProductErrors });

						if (this.activity.activeBlocks().length) this.activity.refreshActiveBlocks();

						this.activity.requestCanvasUpdate([planogram]).subscribe({
							complete: () => this.stopProductMove()
						});
						this.activity.historyGenerate$.next();
					});

					node.on('mouseenter', () => {
						this._canvas.nativeElement.style.cursor = sizesCursor[node.id()];
					});

					node.on('mouseleave', () => {
						this._canvas.nativeElement.style.cursor = 'default';
					});
				});
			}

			if (!this.editable() && b.products.length) {
				this.canvasDrawerService.getCanvasBlockBody(b).hide();
			}

			this._blocksLayer.add(canvasBlock);
		});

		if (this.activity.activeBlocks().length) {
            this.canvasDrawerService.getCanvasBlock(this.activity.activeBlocks()[0]).moveToTop();
        }
		this._blocksLayer.batchDraw();
	}

	private renderPOSMaterialsLayer(): void {
		this.clearPOSMaterialsLayer();
        if (!this.posMaterialsMode()) return;

		const planogram: IPlanogram = this.planogram();

		for (const material of planogram.stand.POSMaterials) {
			const canvasMaterial: Konva.Group = this.canvasDrawerService.createCanvasPOSMaterial({
				material: material,
				beginPoint: planogram.beginPoint,
				scale: planogram.scale,
				zoom: this.zoom(),
				draggable: !this.modes.drawBlockMode() && !this.modes.equipmentEditMode() && this.editable(),
				active: this.activity.isPOSMaterialActive(material),
				canvasElement: this._canvas.nativeElement,
				tooltipGroup: this._tooltipsLayer as unknown as Konva.Group
			});

			canvasMaterial.off('click dragstart dragmove dragend');

		if (!this.modes.drawBlockMode() && !this.modes.equipmentEditMode() && this.editable()) {
			canvasMaterial.on('click', (event: Konva.KonvaEventObject<MouseEvent>) => {
				this.activity.setPOSMaterialActive(
					material,
					navigator.platform == 'MacIntel' ? event.evt.metaKey : event.evt.ctrlKey
				);
			});

			canvasMaterial.on('dragstart', (event: Konva.KonvaEventObject<MouseEvent>) => {
				if (this._posMaterialRotating || event.target.name() === 'pos-material-rotation-circle') return;
				this.activity.setPOSMaterialActive(material);
				this.clearPosDragOutsidePreviews();
			});

				canvasMaterial.on('dragmove', (event: Konva.KonvaEventObject<MouseEvent>) => {
					if (this._posMaterialRotating || event.target.name() === 'pos-material-rotation-circle') return;
					this.updatePosDragOutsidePreviews(material, canvasMaterial);
				});

				canvasMaterial.on('dragend', (event: Konva.KonvaEventObject<MouseEvent>) => {
					if (this._posMaterialRotating || event.target.name() === 'pos-material-rotation-circle') {
						this.clearPosDragOutsidePreviews();
						return;
					}
					const dropCanvases: Array<HTMLElement> = DragOutsideService.getDropTargetCanvases();
					const targetCanvas: HTMLElement = this.dragOutsideService.findPosTargetCanvas(
						material,
						dropCanvases,
						this._canvas.nativeElement
					);

					if (targetCanvas !== this._canvas.nativeElement) {
                        if (targetCanvas.dataset.hasOwnProperty('canvasDetailedElement')) {
                            this.dragOutsideService.clearPos();
                            this.renderPOSMaterialsLayer();
                            return;
                        }
                        this.dragOutsideService.posDrop$.next(targetCanvas);
						return;
					}

					material.x = Utils.round((canvasMaterial.x() - planogram.beginPoint.x - canvasOffset) / planogram.scale);
					material.y = Utils.round((canvasMaterial.y() - planogram.beginPoint.y - canvasOffset) / planogram.scale);
					this.clearPosDragOutsidePreviews();
					this.activity.historyGenerate$.next();
					this.canvasUpdate([], [PlanogramCanvasLayer.PosMaterials]);
				});

				const rotationCircle: Konva.Circle = canvasMaterial.findOne<Konva.Circle>('.pos-material-rotation-circle');
				const textLabel: Konva.Text = canvasMaterial.findOne<Konva.Text>('.pos-text-material-text');

				if (textLabel) {
					textLabel.off('dblclick');

					textLabel.on('dblclick', () => {
						if (!this.activity.isPOSMaterialActive(material)) this.activity.setPOSMaterialActive(material);

						const container: HTMLDivElement = this._mainStage.container();
						const rect: DOMRect = container.getBoundingClientRect();
						const rotation: number = canvasMaterial.getAbsoluteRotation();
						const padding: number = 2;
						const materialHeight: number = canvasMaterial.height();
						const textMaterial: POSTextMaterial | null = material instanceof POSTextMaterial ? material : null;
						const textarea: HTMLTextAreaElement = document.createElement('textarea');
						textarea.value = textLabel.text();
						textarea.style.position = 'absolute';
						textarea.style.fontSize = `${textLabel.fontSize()}px`;
						textarea.style.fontFamily = textLabel.fontFamily();
						textarea.style.fontStyle = textLabel.fontStyle();
						textarea.style.lineHeight = textLabel.lineHeight().toString();
						textarea.style.textAlign = textLabel.align();
						textarea.style.color = 'transparent';
						textarea.style.caretColor = textLabel.fill().toString();
						textarea.style.background = 'transparent';
						textarea.style.border = 'none';
						textarea.style.padding = padding + 'px';
						textarea.style.boxSizing = 'border-box';
						textarea.style.margin = '0';
						textarea.style.outline = 'none';
						textarea.style.zIndex = '1000';
						textarea.style.transformOrigin = 'top left';
						textarea.style.transform = `rotate(${rotation}deg)`;
						textarea.style.overflow = 'hidden';
						textarea.style.resize = 'none';
						textarea.style.whiteSpace = 'pre-wrap';

						const applyVerticalAlign = (): void => {
							const textHeight: number = textLabel.height();
							switch (textMaterial?.verticalAlign) {
								case POSTextVerticalAlign.Middle:
									textLabel.y(materialHeight / 2 - textHeight / 2);
									break;
								case POSTextVerticalAlign.Bottom:
									textLabel.y(materialHeight - textHeight);
									break;
								default:
									textLabel.y(0);
									break;
							}
						};

						const syncTextareaPosition = (): void => {
							const textPosition: Point = textLabel.getAbsolutePosition();
							textarea.style.top = `${rect.top + textPosition.y}px`;
							textarea.style.left = `${rect.left + textPosition.x}px`;
							textarea.style.width = `${textLabel.width()}px`;
							textarea.style.height = `${textLabel.height()}px`;
						};

						const syncTextLabelFromTextarea = (): void => {
							if (textarea.value.length > GlobalSettings.MORE_SYMBOL_VALIDATION) {
								textarea.value = textarea.value.slice(0, GlobalSettings.MORE_SYMBOL_VALIDATION);
							}

							textLabel.text(textarea.value);
							applyVerticalAlign();
							syncTextareaPosition();
							this._posMaterialsLayer.batchDraw();
						};

						syncTextLabelFromTextarea();
						document.body.appendChild(textarea);
						textarea.focus();

						textarea.addEventListener('input', syncTextLabelFromTextarea);

						textarea.addEventListener('keydown', (e) => {
							if (e.key === 'Escape') {
								textarea.blur();
								e.stopPropagation();
							}
						});

						textarea.addEventListener('blur', () => {
							const prevValue: string = material.name;
							const value: string = textarea.value || prevValue;
							textarea.remove();
							material.name = value;
							clearInterval(checkZoomInterval);
							if (prevValue !== material.name) this.activity.historyGenerate$.next();
							this.canvasUpdate([], [PlanogramCanvasLayer.PosMaterials]);
							this.activity.setPOSMaterialActive(material);
						});

						const currentPixelRatio: number = window.devicePixelRatio;
						const currentZoom: number = this.zoom();
						const checkZoomInterval = setInterval(() => {
							if (window.devicePixelRatio !== currentPixelRatio || this.zoom() !== currentZoom) textarea.blur();
						}, 500);
					});
				}

				if (rotationCircle) {
					rotationCircle.off('mouseenter mouseleave mousedown touchstart dragstart dragmove dragend');

					rotationCircle.on('mouseenter', () => this.showRotateCursor.set(true));
					rotationCircle.on('mouseleave', () => this.showRotateCursor.set(false));

					rotationCircle.on('mousedown touchstart', (event: Konva.KonvaEventObject<MouseEvent>) => {
						event.cancelBubble = true;
						this._posMaterialRotating = true;
						this.clearPosDragOutsidePreviews();
					});

					rotationCircle.on('mouseup touchend', () => {
						if (!rotationCircle.isDragging()) this._posMaterialRotating = false;
					});

					rotationCircle.on('dragstart', (event: Konva.KonvaEventObject<MouseEvent>) => {
						event.cancelBubble = true;
						this._posMaterialRotating = true;
						this.clearPosDragOutsidePreviews();
					});

					rotationCircle.on('dragmove', (event: Konva.KonvaEventObject<MouseEvent>) => {
						this.showRotateCursor.set(true);
						const x: number = event.evt.offsetX;
						const y: number = event.evt.offsetY;
						const rotationXPoint: number = material.x * planogram.scale + canvasOffset + planogram.beginPoint.x;
						const rotationYPoint: number = material.y * planogram.scale + canvasOffset + planogram.beginPoint.y;
						const radians: number = Math.atan2(x - rotationXPoint, y - rotationYPoint);
						let angle: number = Math.round(radians * (180 / Math.PI) * -1 + 90);
						angle -= Math.round(Math.atan(canvasMaterial.height() / canvasMaterial.width()) * 180 / Math.PI);

						if (angle > 85 && angle < 95) angle = 90;
						if (angle < -85 && angle > -95) angle = -90;
						if (angle < 5 && angle > -5) angle = 0;
						if (angle < 185 && angle > 175) angle = 180;
						if (angle > 180) angle -= 360;

						const baseCorePoint: [number, number] = Utils.rotatePoint(
							[rotationXPoint, rotationYPoint],
							[canvasMaterial.x(), canvasMaterial.y()],
							-canvasMaterial.rotation()
						);
						const newCorePoint: [number, number] = Utils.rotatePoint(
							[rotationXPoint, rotationYPoint],
							baseCorePoint,
							angle
						);

						canvasMaterial.setAttrs({ x: newCorePoint[0], y: newCorePoint[1] });
						canvasMaterial.rotation(angle);
						rotationCircle.setAttrs({
							x: canvasMaterial.width(),
							y: canvasMaterial.height()
						});
					});

					rotationCircle.on('dragend', () => {
						this.showRotateCursor.set(false);
						this.clearPosDragOutsidePreviews();
						const angle: number = canvasMaterial.rotation();
						if (material.angle === angle) {
							queueMicrotask(() => { this._posMaterialRotating = false; });
							return;
						}
						material.angle = angle;
						this.activity.setPOSMaterialActive(material);
						queueMicrotask(() => { this._posMaterialRotating = false; });
					});
				}
			}

			this._posMaterialsLayer.add(canvasMaterial);
		}

		this._posMaterialsLayer.batchDraw();
	}

	renderSizesLayer(_elements: Array<string> = []): void {
        if (!this._mainStage || this.hideCanvas()) return;
        this.clearSizesLayer();
		if (!this.sizesMode()) return;

		const planogram: IPlanogram = this.planogram();
		this._sizesLayer.add(...this.canvasDrawerService.createPlanogramSizes({
			element: null,
			beginPoint: planogram.beginPoint,
			scale: planogram.scale,
			zoom: this.zoom(),
			active: false,
			flip: this.flipMode()
		}, planogram.stand));
		this._sizesLayer.draw();
	}

	renderTooltipsLayer(elements: Array<string> = []): void {
		const planogram: IPlanogram = this.planogram();
		if (!planogram?.visible) return;

		this.clearTooltipsLayer(elements);
		this.forEachEquipmentElement(elements, (element: Element) => this.drawTooltipElement(planogram, element));
		this.drawProductTooltips(elements);
		this._tooltipsLayer.batchDraw();
	}

	renderToolsLayer(elements: Array<string> = []): void {
		this.clearToolsLayer();

		if (
			this.modes.equipmentEditMode()
			&& this.activity.shouldShowEquipmentDistanceLines(this.planogram().main_stand)
		) {
			const element: Element | undefined = this.planogram().stand.getElementById(this.activity.selectedElement());
			if (element) this.addDistanceLines(element);
		}

		this._toolsLayer.draw();
	}

	private clearPlanogramLayer(elements: Array<string> = []): void {
		this.clearLayerNodes(this._planogramLayer, elements);
	}

	private clearBlocksLayer(elements: Array<string> = []): void {
		this.clearLayerNodes(this._blocksLayer, elements, this.blocksOverlayNodes);
	}

	private clearPOSMaterialsLayer(): void {
		this.clearLayerNodes(this._posMaterialsLayer);
		this._posMaterialsLayer.draw();
	}

	private clearSizesLayer(elements: Array<string> = []): void {
		this.clearLayerNodes(this._sizesLayer, elements);
		this._sizesLayer.draw();
	}

	private clearTooltipsLayer(elements: Array<string> = []): void {
		this.clearLayerNodes(this._tooltipsLayer, elements);
	}

	private clearToolsLayer(elements: Array<string> = []): void {
		this.clearLayerNodes(this._toolsLayer, elements);
		this._toolsLayer.draw();
	}

	private get blocksOverlayNodes(): Array<Konva.Node> {
		return this._blockDrawOverlay?.nodes ?? [];
	}

	private clearLayerNodes(layer: Konva.Layer, elements: Array<string> = [], reserved: Array<Konva.Node> = []): void {
		const reservedNodes: Set<Konva.Node> = new Set(reserved);

		[...layer.getChildren()].forEach((node: Konva.Node) => {
			if (reservedNodes.has(node)) return;

			if (!elements.length || (node.id() !== '' && elements.includes(node.id()))) {
				node.destroy();
			}
		});
	}

	private forEachEquipmentElement(elements: Array<string>, callback: (element: Element) => void): void {
		const planogram: IPlanogram = this.planogram();
		const orderedElements: Array<Element> = (planogram.stand as { orderedElements?: Array<Element> }).orderedElements
			?? planogram.stand.allElements;

		orderedElements
			.filter((element: Element) => !element.is(HoleElement))
			.forEach((element: Element) => {
				if (!elements.length || elements.includes(element.id)) callback(element);
			});
	}

	private drawDistanceLines(
		element: Element = null,
		normalize: Point = { x: 0, y: 0 },
		delta: Point = { x: 0, y: 0 }
	): void {
		this.clearToolsLayer();
		this.addDistanceLines(element, normalize, delta);
		this._toolsLayer.draw();
	}

	private addDistanceLines(
		element: Element = null,
		normalize: Point = { x: 0, y: 0 },
		delta: Point = { x: 0, y: 0 }
	): void {
		if (!element) return;

		const planogram: IPlanogram = this.planogram();
		const distanceLines: Array<Konva.Line | Konva.Label> = this.canvasDrawerService.createEquipmentDistanceLines({
			element: element,
			beginPoint: planogram.beginPoint,
			scale: planogram.scale,
			normalize: normalize,
			active: true,
			flip: this.flipMode()
		}, planogram.stand, false, delta);

		if (distanceLines.length) this._toolsLayer.add(...distanceLines);
	}

	private drawPlanogramElement(element: Element): void {
		const planogram: IPlanogram = this.planogram();
		const canvasElement: Konva.Group = this.canvasDrawerService.createCanvasEquipmentElement(
			{
				element: element,
				beginPoint: planogram.beginPoint,
				scale: planogram.scale,
				active: false,
				flip: this.flipMode()
			},
			planogram.stand
		);

		canvasElement.id(element.id);

		if (this.transparentBoxesMode()) {
			canvasElement.find('.back-wall').forEach((shape: Konva.Shape) => shape.fill('transparent'));
			canvasElement.find('.front-wall').forEach((shape: Konva.Shape) => shape.fill('transparent'));
		}

		if (this.activity.targetElement() == element) {
			this.canvasDrawerService.setEquipmentElementHovered(element);
		} else {
			this.canvasDrawerService.setEquipmentElementInactive(element);
		}

		canvasElement.off('click dblclick mouseenter mouseleave');
		canvasElement.on('click', () => {
			if (['RectangleBase', 'TrapezeBase', 'Shelf', 'AngleShelf', 'HangBar', 'Hook', 'Perforation', 'PerforationWall'].includes(element.parent.type)) {
				this.canvasDrawerService.setEquipmentElementInactive(this.activity.targetElement());
				this.activity.setTargetElement(element);
			}

			if (!this.editable() || !this.modes.equipmentEditMode()) return;

			if (element.parent.is(...DRAGGABLE_ELEMENTS.front)) {
				this.activity.setFocusedCanvas(this.planogram().main_stand, null, this._canvas.nativeElement);
				this.activity.setSelectedElement(element.id);
				this.drawDistanceLines(element);
			} else {
				this.activity.clearSelectedElement();
				this.drawDistanceLines();
			}
		});
		canvasElement.on('dblclick', () => {
			this.openDetailedElementWidget(element);
		});

		if (this.modes.equipmentEditMode() && element.parent.is(...DRAGGABLE_ELEMENTS.front)) {
			let relatedElements: Array<Element> = [],
				relatedProducts: Array<ProductGroup> = [],
				xMovement: number = 0,
				yMovement: number = 0,
				prevX: number = 0,
				prevY: number = 0;

			canvasElement.draggable(this.editable());
			canvasElement.off('dragstart dragmove dragend');
			canvasElement.on('dragstart', () => {
				if (['RectangleBase', 'TrapezeBase', 'Shelf', 'AngleShelf', 'HangBar', 'Hook', 'Perforation', 'PerforationWall'].includes(element.parent.type)) {
					this.canvasDrawerService.setEquipmentElementInactive(this.activity.targetElement());
					this.activity.setTargetElement(element);
				}
				relatedElements = element.relatedElements;
				relatedProducts = [...element.products, ...relatedElements.map((el: Element) => el.products).flat()].flat();
				prevX = canvasElement.x();
				prevY = canvasElement.y();
				this.activity.setFocusedCanvas(planogram.main_stand, null, this._canvas.nativeElement);
				this.activity.setSelectedElement(element.id);
				this.drawDistanceLines(element);

				const tooltip: Konva.Label = this._tooltipsLayer.findOne(`#${element.id}`) as Konva.Label;

				if (tooltip) {
					tooltip.hide();
					this._tooltipsLayer.draw();
				}
			});
			canvasElement.on('dragmove', () => {
				const dependElement: Konva.Group = this.canvasDrawerService.getCanvasEquipmentElement(element.depend);

				if (element.depend?.parent.is(EquipmentElements.CentralPillar) || element.shapes[0].type == 'trapeze') {
					canvasElement.x(prevX);
				} else {
					if (canvasElement.x() < dependElement.x()) canvasElement.x(dependElement.x());
					if (canvasElement.x() + canvasElement.width() > dependElement.x() + dependElement.width()) {
						canvasElement.x(dependElement.x() + dependElement.width() - canvasElement.width());
					}
				}
				if (element.parent.is(EquipmentElements.CentralPillar)) {
					canvasElement.y(prevY);
				} else {
					if (canvasElement.y() < dependElement.y()) canvasElement.y(dependElement.y());
					if (canvasElement.y() + canvasElement.height() > dependElement.y() + dependElement.height()) {
						canvasElement.y(dependElement.y() + dependElement.height() - canvasElement.height());
					}
				}

				relatedElements.forEach((el: Element) => {
					const elem: Konva.Group = this.canvasDrawerService.getCanvasEquipmentElement(el);

					elem.offsetX(prevX - canvasElement.x());
					elem.offsetY(prevY - canvasElement.y());
				});
				relatedProducts.forEach((pg: ProductGroup) => {
					const productGroup: Konva.Group = this.canvasDrawerService.getCanvasProduct(pg);

					productGroup.offsetX(prevX - canvasElement.x());
					productGroup.offsetY(prevY - canvasElement.y());
				});
				this.drawDistanceLines(element, { x: 0, y: 0 }, { x: -prevX + canvasElement.x(), y: -prevY + canvasElement.y() });
				this._planogramLayer.batchDraw();
			});
			canvasElement.on('dragend', () => {
				const dependElement: Konva.Group = this.canvasDrawerService.getCanvasEquipmentElement(element.depend);

				if (element.depend?.parent.is(EquipmentElements.CentralPillar) || element.shapes[0].type == 'trapeze') {
					canvasElement.x(prevX);
				} else {
					if (canvasElement.x() < dependElement.x()) canvasElement.x(dependElement.x());
					if (canvasElement.x() + canvasElement.width() > dependElement.x() + dependElement.width()) {
						canvasElement.x(dependElement.x() + dependElement.width() - canvasElement.width());
					}
				}
				if (element.parent.is(EquipmentElements.CentralPillar)) {
					canvasElement.y(prevY);
				} else {
					if (canvasElement.y() < dependElement.y()) canvasElement.y(dependElement.y());
					if (canvasElement.y() + canvasElement.height() > dependElement.y() + dependElement.height()) {
						canvasElement.y(dependElement.y() + dependElement.height() - canvasElement.height());
					}
				}

				xMovement = Utils.round((canvasElement.x() - prevX) / planogram.scale);
				yMovement = Utils.round((prevY - canvasElement.y()) / planogram.scale);

				if (
					element.parent.is(...[EquipmentElements.Shelf, EquipmentElements.AngleShelf, EquipmentElements.HangBar])
					&& element.depend.isExtensionActive(ExtensionType.Rails)
				) {
					const anchorPoint: number = Utils.getNearestPoint(
						elementsGenerator[EquipmentElements.BackWall].getAnchorPoints(element.depend),
						element.getBoundingY().min + yMovement
					);

					yMovement = Utils.round(yMovement - (element.getBoundingY().min + yMovement - anchorPoint));
				}

				if (element.parent.is(EquipmentElements.Hook) && element.depend.parent.is(EquipmentElements.HangBar)) {
					yMovement = 0;
				}

				element.shapes[0].drag(xMovement, yMovement);

				if (element.parent.is(EquipmentElements.AngleShelf)) {
					(element.shapes[0] as Rectangle).updateRotationPoint(planogram.stand, element);
				}

				relatedElements.forEach((el: Element) => el.shapes[0].drag(xMovement, yMovement));
				relatedProducts.forEach((pg: ProductGroup) => pg.move(xMovement, yMovement));
				this.finalizeEquipmentMove(planogram);
			});
		}

		if (['RectangleBase', 'TrapezeBase', 'Shelf', 'AngleShelf', 'HangBar', 'Hook'].includes(element.parent.type)) {
			canvasElement.on('mouseenter', () => {
				this.canvasDrawerService.setEquipmentElementHovered(element);
				this._planogramLayer.draw();
			});
			canvasElement.on('mouseleave', () => {
				if (element == this.activity.targetElement()) return;

				this.canvasDrawerService.setEquipmentElementInactive(element);
				this._planogramLayer.draw();
			});
		}

		this._planogramLayer.add(canvasElement);
	}

	private drawTooltipElement(stand: IPlanogram, element: Element): void {
		const canvasElement: Konva.Group = this.canvasDrawerService.getCanvasEquipmentElement(element);
		const tooltip: Konva.Label = this.canvasDrawerService.createCanvasEquipmentElementTooltip(
			{
				element: element,
				beginPoint: stand.beginPoint,
				scale: stand.scale,
				active: false,
				flip: this.flipMode()
			},
			stand.stand,
			{
				SHELF: this.translate.instant('SHELF'),
				HANG_BAR: this.translate.instant('HANG_BAR'),
				BOX: this.translate.instant('BOX'),
				ONE_HOOK: this.translate.instant('ONE_HOOK'),
				LENGTH: this.measurementUnitPipe.transform(this.translate.instant('LENGTH'), 'distance'),
				DEPTH: this.measurementUnitPipe.transform(this.translate.instant('DEPTH'), 'distance'),
				LINEAR_METERS: this.translate.instant('LINEAR_METERS', { unit: this.measurementUnitTranslatePipe.transform('distance', false, 'feet') }),
				FREE_SPACE_ON_THE_SHELF_M: this.measurementUnitPipe.transform(this.translate.instant('FREE_SPACE_ON_THE_SHELF_M'), 'distance'),
				FREE_SPACE_ON_THE_HANG_BAR_M: this.measurementUnitPipe.transform(this.translate.instant('FREE_SPACE_ON_THE_HANG_BAR_M'), 'distance'),
				FREE_SPACE_IN_THE_BOX_M: this.measurementUnitPipe.transform(this.translate.instant('FREE_SPACE_IN_THE_BOX_M'), 'distance'),
				FREE_SPACE_ON_THE_HOOK_M: this.measurementUnitPipe.transform(this.translate.instant('FREE_SPACE_ON_THE_HOOK_M'), 'distance'),
				QUANTITY_OF_LAID_OUT_SKU: this.translate.instant('QUANTITY_OF_LAID_OUT_SKU')
			}
		);

		if (!tooltip) return;

		tooltip.id(element.id);
		this._tooltipsLayer.add(tooltip);

		canvasElement.on('mouseenter', () => {
			tooltip.show();
			this._tooltipsLayer.draw();
		});
		canvasElement.on('mouseleave', () => {
			tooltip.hide();
			this._tooltipsLayer.draw();
		});
	}

	private initCanvasUpdateHandler(): void {
		this.activity.canvasUpdate$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((req: CanvasUpdateRequest) => {
				const planogram: IPlanogram = this.planogram();
				if (req.stands?.length && !req.stands.some((stand: IPlanogram) => stand.id === planogram?.id)) return;

				this.canvasUpdate(req.ids ?? [], req.layers);

				if (req.requestId) {
					requestAnimationFrame(() => this.activity.canvasUpdateCompleted$.next(req));
				}
			});
	}

	private initHighlightUpdateHandler(): void {
		this.activity.highlightUpdate$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.refreshHighlightLegend(true));
	}

	private initPerforationNumbersHandler(): void {
		this.activity.setNumbersForPerforation$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((elements: Array<Element>) => this.updatePerforationProductNumbers(elements));
	}

	private updatePerforationProductNumbers(elements: Array<Element>): void {
		const planogram: IPlanogram = this.planogram();
		const perforations: Array<Element> = Utils.unique(
			elements.filter((el: Element) => el.is(HoleElement)).map((el: Element) => el.depend)
		);
		const productsOnHoles: Array<ProductGroup> = planogram.stand.products
			.filter((pg: ProductGroup) => pg.element.is(HoleElement) && perforations.includes(pg.element.depend));

		perforations.forEach((el: Element) => {
			const resetPg: Array<ProductGroup> = productsOnHoles
				.filter((pg: ProductGroup) => pg.element.depend == el)
				.sort((a: ProductGroup, b: ProductGroup) => {
					return Utils.round(b.begin.y + b.Height) - Utils.round(a.begin.y + a.Height) ||
						a.element.shapes[0].begin.x - b.element.shapes[0].begin.x;
				});

			resetPg.forEach((pg: ProductGroup, idx: number) => {
				if (!elements.includes(pg.element)) {
					this.canvasDrawerService.drawProductNumber({
						productGroup: pg,
						stand: planogram.stand,
						scale: planogram.scale,
						beginPoint: planogram.beginPoint
					}, this.canvasDrawerService.getCanvasProduct(pg), idx + 1);
				}
			});
		});
	}

	private initProductDragHandlers(): void {


		this.dragOutsideService.redrawCanvas$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.redrawProductsCanvas());
	}

	private initPOSDragHandlers(): void {

	}

	private drawProducts(elements: Array<string> = []): void {
		const planogram: IPlanogram = this.planogram();
		planogram.stand.products
			.sort((a: ProductGroup, b: ProductGroup) => a.begin.z - b.begin.z || a.begin.x - b.begin.x || a.begin.y - b.begin.y)
			.forEach((pg: ProductGroup) => {
				if (!elements.length || elements.includes(pg.element.id)) this.drawProductGroup(pg);
			});
	}

	private drawProductTooltips(elements: Array<string> = []): void {
		const planogram: IPlanogram = this.planogram();

		planogram.stand.products.forEach((pg: ProductGroup) => {
			if (!elements.length || elements.includes(pg.element.id)) this.setupProductTooltip(planogram, pg);
		});
	}

	private getProductDataObject(planogram: IPlanogram, productGroup: ProductGroup): Record<string, unknown> {
		let dataObject: Record<string, unknown> = {};

		if (!this.editable()) {
			const standData = this.activity.getStandData(planogram.id);

			if (standData?.[productGroup.product.id]) {
				Object.assign(dataObject, standData[productGroup.product.id]);
			}
		}

		if (this.activity.isProductHighlighted(productGroup.product.id)) {
			dataObject = this.highlightProductsService.getCorrectHighlight(
				this.activity.highlightValue(),
				this.activity.highlightedProducts(),
				planogram.stand.products,
				productGroup
			);
		} else if (this.activity.isProductRotated(productGroup.product.id)) {
			dataObject = this.activity.rotatedProducts()[productGroup.product.id];
		} else if (this.activity.isProductInRule(productGroup.product.id)) {
			const object = this.activity.layoutRulesProducts()[productGroup.product.id],
				property = ['ShelfElement', 'PalletElement'].includes(productGroup.element.constructor.name)
					? Utils.getElementIndex(productGroup.element, planogram.stand).toString()
					: 'default';

			dataObject = object[property] || object['default'] || {};
		}

		if (
			!this.editable()
			&& (!this.activity.labelsState().price || this.activity.filterType() === 'lost-sales')
			&& this.activity.filterType() !== 'distribution'
			&& this.activity.filterType() !== 'square'
		) {
			delete dataObject.price;
		}

		return dataObject;
	}

	private drawProductGroup(productGroup: ProductGroup): void {
        const planogram: IPlanogram = this.planogram();
		const dataObject: Record<string, unknown> = this.getProductDataObject(planogram, productGroup);

		if (this.activity.isProductActive(productGroup) || this.activity.isProductActive(productGroup.dependProduct)) {
			this.canvasDrawerService.getCanvasProduct(productGroup).destroyChildren();
		}

		const canvasProduct: Konva.Group = this.canvasDrawerService.createCanvasProduct({
			productGroup: productGroup,
			stand: planogram.stand,
			scale: planogram.scale,
			beginPoint: planogram.beginPoint,
			articleMode: !this.imagesMode(),
			sequenceNumberMode: this.sequenceNumberMode(),
			flipMode: this.flipMode(),
			dataObject: dataObject,
			showStockQty: this.activity.labelsState().stockQty,
			showPrices: this.activity.labelsState().price,
			showAbcTurnover: this.activity.labelsState().abcTurnover,
			showAbcReceipts: this.activity.labelsState().abcReceipts,
			zoom: this.zoom(),
			callback: () => {
				if (this.activity.isProductActive(productGroup)) {
					this.canvasDrawerService.setCanvasProductActive(productGroup, canvasProduct);
				}

				this._planogramLayer.draw();
			}
		});

		canvasProduct.off('click dblclick dragstart dragmove dragend mouseenter mouseleave');
		this._planogramLayer.add(canvasProduct);

		if (!this.editable()) {
			this.drawProductLabels(planogram, productGroup, canvasProduct, dataObject);
		}

		if (this.activity.isProductActive(productGroup)) {
			this.canvasDrawerService.setCanvasProductActive(productGroup, canvasProduct);
		}

		let prevX: number = canvasProduct.x(), prevY: number = canvasProduct.y();

		canvasProduct.draggable(!this.modes.drawBlockMode() && !this.modes.equipmentEditMode() && this.editable());

        canvasProduct.on('click', (event: Konva.KonvaEventObject<MouseEvent>) => {
            if (!this.editable()) return;
            this.activity.setProductActive(productGroup, navigator.platform == 'MacIntel' ? event.evt.metaKey : event.evt.ctrlKey);
            this.activity.syncProducts(productGroup, event.evt);
        });

        canvasProduct.on('dblclick', () => {
            if (!this.editable()) return;
            this.activity.changeProductPosition(productGroup);
        });

		canvasProduct.on('dragstart', (event: Konva.KonvaEventObject<DragEvent>) => {
			this.activity.retainActiveProductsForStand(planogram.stand);
			this.activity.setProductMove(true);
			this.clearDragOutsidePreviews();

			let dependedProducts: Array<ProductGroup> = [];

			if (!this.activity.isProductActive(productGroup)) {
				this.activity.clearActiveProducts();
			}

			if (this.activity.activeProducts().length) {
				[...this.activity.activeProducts().filter((el) => el !== productGroup), productGroup].forEach((el) => {
					if (this.activity.isProductActive(el.dependProduct)) return;

					if (el.element.parent.type != 'Hook') {
						dependedProducts = [
							...dependedProducts,
							...Utils.findDependProducts(el, planogram.stand.products)
						];
					}
				});
			} else {
				this.activity.clearActiveProducts();
				dependedProducts = productGroup.element.parent.type == 'Hook'
					? []
					: Utils.findDependProducts(productGroup, planogram.stand.products);
			}

			this.canvasDrawerService.getCanvasProductTooltip(productGroup).hide();

			if (dependedProducts.length) {
				[productGroup, ...dependedProducts]
					.filter((pg) => !this.activity.isProductActive(pg))
					.forEach((pg) => this.activity.setProductActive(pg, true));
			} else if (!this.activity.isProductActive(productGroup)) {
				this.activity.setProductActive(productGroup, true);
			}

			this.activity.activeProducts()
				.sort((a: ProductGroup, b: ProductGroup) => a.getBoundingZ().max - b.getBoundingZ().max)
				.map((pg: ProductGroup) => this.canvasDrawerService.getCanvasProduct(pg))
				.forEach((group: Konva.Group) => {
					group.moveToTop();
					group.find('.hanger').forEach((canvasHanger: Konva.Group) => canvasHanger.hide());
				});

			this.activity.syncProducts(productGroup, event.evt);
			prevX = canvasProduct.x();
			prevY = canvasProduct.y();
		});

		canvasProduct.on('dragmove', () => {
			const diffX: number = canvasProduct.x() - prevX, diffY: number = canvasProduct.y() - prevY;

			this.activity.activeProducts()
				.filter((pg: ProductGroup) => pg != productGroup)
				.forEach((pg: ProductGroup) => {
					const konvaEl: Konva.Group = this.canvasDrawerService.getCanvasProduct(pg);

					konvaEl.x(konvaEl.x() + diffX);
					konvaEl.y(konvaEl.y() + diffY);
				});

			prevX = canvasProduct.x();
			prevY = canvasProduct.y();
			this.updateDragOutsidePreviews();
		});

		canvasProduct.on('dragend', () => {
			const dropCanvases: Array<HTMLElement> = DragOutsideService.getDropTargetCanvases();
			const targetCanvas: HTMLElement = this.dragOutsideService.emitDropIfTargetDifferent(
				this.activity.activeProducts(),
				dropCanvases,
				this._canvas.nativeElement
			);

			if (targetCanvas) return this.redrawProductsCanvas();

			const behavior: PlanogramViewBehavior = PlanogramViewBehaviorFactory
				.createBehavior(planogram, null, planogram.scale, planogram.beginPoint);

			this.handleProductGroupDrop(behavior);
		});

		canvasProduct.on('mouseenter', () => {
			this.canvasDrawerService.getCanvasProductTooltip(productGroup).show();
			this._tooltipsLayer.batchDraw();
		});

		canvasProduct.on('mouseleave', () => {
			this.canvasDrawerService.getCanvasProductTooltip(productGroup).hide();
			this._tooltipsLayer.batchDraw();
		});
	}

	private drawProductLabels(
		planogram: IPlanogram,
		productGroup: ProductGroup,
		canvasProduct: Konva.Group,
		dataObject: Record<string, unknown>
	): void {
		const labelsState = this.activity.labelsState();
		const shouldShowLabel: boolean = labelsState.articles
			|| Boolean(dataObject.promo_product)
			|| Boolean(dataObject.new_product);

		if (!shouldShowLabel) return;

		const hasProductAbove: boolean = planogram.stand.products.some((pg: ProductGroup) =>
			pg.product.id !== productGroup.product.id &&
			pg.element.idx === productGroup.element.idx &&
			pg.begin.z > productGroup.begin.z &&
			pg.begin.x === productGroup.begin.x
		);

		if (hasProductAbove) return;

		let labelText = '';
		let labelFill = 'white';

		if (dataObject.promo_product) {
			labelText = PRODUCT_LABELS_DICT.PROMO;
			labelFill = 'red';
		}

		if (dataObject.new_product) {
			labelText = PRODUCT_LABELS_DICT.NEW;
			labelFill = '#ECC532';
		}

		if (labelsState.articles) {
			labelText = String(productGroup.product.article);
			labelFill = 'white';
		}

		const delta: number = (['Box', 'SlicedBox'].includes(productGroup.element.parent.type) && productGroup.isLooseForm)
			? productGroup.element.shapes[0].wallWidth * planogram.scale
			: 0;

		const labelGroup: Konva.Group = this.canvasDrawerService.drawTextLabel({
			productGroup,
			stand: planogram.stand,
			scale: planogram.scale,
			beginPoint: planogram.beginPoint,
			zoom: this.zoom()
		}, canvasProduct, 'label', labelText, labelFill);

		labelGroup.setAttrs({
			x: canvasProduct.width() / 2 - labelGroup.width() / 2,
			y: canvasProduct.height() + delta
		});
	}

	private setupProductTooltip(planogram: IPlanogram, productGroup: ProductGroup): void {
		const dataObject: Record<string, unknown> = this.getProductDataObject(planogram, productGroup);

		const tooltip$ = this.editable()
			? this.planogramTooltipService.buildTooltipText(
				productGroup,
				dataObject,
				this.sessionService.personalSettings.planogramEditTooltipFields,
				{
					abcTurnover: this.activity.labelsState().abcTurnover,
					abcReceipts: this.activity.labelsState().abcReceipts,
					multiplePlanograms: this.activity.allPlanograms().length > 1,
				}
			)
			: this.planogramTooltipService.buildViewTooltipText(
				productGroup,
				dataObject,
				this.activity.filterType(),
				{ interval: this.activity.filters().interval, metrics: this.activity.filters().metrics }
			);

		tooltip$.subscribe((tooltip: string) => {
			const tooltipLabel: Konva.Label = this.canvasDrawerService.createCanvasProductTooltip(
				productGroup,
				tooltip,
				this._canvas.nativeElement
			);

			this._tooltipsLayer.add(tooltipLabel);
		});
	}

	private redrawProductsCanvas(): void {
		const elements: Array<Element> = Utils.unique(
			this.activity.activeProducts().map((pg: ProductGroup) => [pg.element, ...pg.element.relatedElements]).flat()
		);

		this.clearDragOutsidePreviews();
		this.canvasUpdate(elements.map((el: Element) => el.id));
		this.stopProductMove();
	}

	private stopProductMove(): void {
		this.activity.setProductMove(false);
		this.clearDragOutsidePreviews();
		this.clearBlockDragOutsidePreviews();
	}

	private updateDragOutsidePreviews(): void {
		if (!this._mainStage || !this._canvasContainer?.nativeElement) return;

		this.dragOutsideService.update({
			items: this.activity.activeProducts().map((pg: ProductGroup) => ({
				key: pg,
				canvasProduct: this.canvasDrawerService.getCanvasProduct(pg)
			})),
			stage: this._mainStage,
			container: this._canvasContainer.nativeElement,
			overCanvasTargets: Array.from(document.querySelectorAll('detailed-element-widget'))
				.filter((el) => el instanceof HTMLElement) as Array<HTMLElement>
		});
	}

	private clearDragOutsidePreviews(): void {
		this.dragOutsideService.clear();
	}

	private getCorrectPoint(behavior: PlanogramViewBehavior, pg: ProductGroup, dragOutside: boolean = false): Point {
		let point: Point = null;

		if (dragOutside) {
			point = behavior.convertCanvasPointToPlanogram(this.dragOutsideService.getDropPoint(pg, this._mainStage));
		}

		if (!point) {
			point = behavior.getPoint(pg, this.canvasDrawerService.getCanvasProduct(pg));
		}

		return point;
	}

	private handleProductGroupDrop(behavior: PlanogramViewBehavior, dragOutside: boolean = false): void {
		if (!this.activity.activeProducts().length) {
			this.stopProductMove();
			return;
		}

        const planogram: IPlanogram = this.planogram();
        if (!planogram) return this.dragOutsideService.redrawCanvas$.next();

		const activeProductGroups: Array<Array<ProductGroup>> = behavior.getActiveProductGroups(this.activity.activeProducts());

		const sourceStand = activeProductGroups[0][0].element.stand;
		const isCrossPlanogram: boolean = sourceStand !== planogram.stand;
		let sourcePlanogram: IPlanogram;

		if (isCrossPlanogram) {
			sourcePlanogram = this.activity.getPlanogramByStand(sourceStand);
			if (!sourcePlanogram) return this.dragOutsideService.redrawCanvas$.next();
		}

		for (const productGroup of activeProductGroups) {
			const point: Point = this.getCorrectPoint(behavior, productGroup[0], dragOutside);
			const element: Element = behavior.getAnchorElement(productGroup[0], point);

			if (!element) return this.dragOutsideService.redrawCanvas$.next();

			point.z = behavior.getCorrectZCoordinate(element, point.z, productGroup[0]);
			const result: ICanPutProductsResult = element.canPutProductGroups(productGroup, point);

			if (!result.success) {
				if (result.message) this.snackBarService.setSnackBarNotification(result.message);
				return this.dragOutsideService.redrawCanvas$.next();
			}
		}

		let sourceNormalizeElements: Array<Element> = [];

		if (isCrossPlanogram) {
			sourceNormalizeElements = this.activity.removeProductsFromStandForTransfer(
				sourceStand,
				Utils.to1DArray(activeProductGroups)
			);
			sourceNormalizeElements = this.activity.normalizeElements(sourceStand, Utils.unique(sourceNormalizeElements));
			this.activity.ensureProductsInPlanogramCatalog(planogram, Utils.to1DArray(activeProductGroups));
		}

		const normalizeList: Array<Element> = activeProductGroups.map((g: Array<ProductGroup>) => g[0].element);

		for (const productGroup of activeProductGroups) {
			const point: Point = this.getCorrectPoint(behavior, productGroup[0], dragOutside);
			const element: Element = behavior.getAnchorElement(productGroup[0], point);

			if (!element) return this.dragOutsideService.redrawCanvas$.next();

			point.z = behavior.getCorrectZCoordinate(element, point.z, productGroup[0]);
			element.putProductGroups(productGroup, activeProductGroups, { ...point });

			const block: Block = planogram.stand.findBlock(point.x, point.y, productGroup[0].Width);

			if (this.activity.selectedBlock() !== block) this.activity.setSelectedBlock(block);

			productGroup.forEach((product: ProductGroup) => normalizeList.push(product.element));
		}

		planogram.stand.shiftProducts(activeProductGroups);

		const changedBlocks: Array<Block> = planogram.stand.blocks
			.filter((block: Block) => block.products.some((pg: ProductGroup) => this.activity.isProductActive(pg)));

		if (changedBlocks.length) {
			changedBlocks.forEach((block: Block) => {
				const blockIndex: number = planogram.stand.blocks.indexOf(block);

				planogram.stand.blocks[blockIndex].updateBlockProducts(
					this.activity.getDependBlockProducts(planogram.stand.blocks[blockIndex], planogram.stand)
				);
			});
		}

		planogram.stand.blocks.forEach((block: Block) => block.clearCache());

		const elements: Array<Element> = this.activity.normalizeElements(planogram.stand, Utils.unique(normalizeList));

		this.activity.setNumbersForPerforation$.next(elements);

		const activeElements: Array<ProductGroup> = [...this.activity.activeProducts()];

		activeElements.forEach((pg: ProductGroup, idx: number) => this.activity.setProductActive(pg, !!idx));

		if (this.activity.activeProducts().length > 1 && this.activity.settingsPanel().differentElements) {
			this.activity.closePanel();
		}

		const updatePlanograms: Array<IPlanogram> = isCrossPlanogram && sourcePlanogram
			? [sourcePlanogram, planogram]
			: [planogram];
		const updateElementIds: Array<string> = isCrossPlanogram
			? Utils.unique([...sourceNormalizeElements, ...elements].map((el: Element) => el.id))
			: elements.map((el: Element) => el.id);

		this.activity.setPlanogram(planogram);

		this.activity.requestCanvasUpdate(updatePlanograms, updateElementIds).subscribe({
			complete: () => this.stopProductMove()
		});

		if (isCrossPlanogram && sourcePlanogram) {
			this.activity.historyGenerate$.next(sourcePlanogram.main_stand);
			this.activity.historyGenerate$.next(planogram.main_stand);
		} else {
			this.activity.historyGenerate$.next();
		}

		this.activity.setFocusedCanvas(planogram.main_stand, null, this._canvas.nativeElement);
	}

	private updatePosDragOutsidePreviews(material: POSMaterial, canvasMaterial: Konva.Group): void {
		if (!this._mainStage || !this._canvasContainer?.nativeElement) return;

		this.dragOutsideService.updatePos({
			items: [{ key: material, canvasItem: canvasMaterial }],
			stage: this._mainStage,
			container: this._canvasContainer.nativeElement,
			overCanvasTargets: Array.from(document.querySelectorAll('detailed-element-widget'))
				.filter((el) => el instanceof HTMLElement) as Array<HTMLElement>
		});
	}

	private clearPosDragOutsidePreviews(): void {
		this.dragOutsideService.clearPos();
	}

	private clearBlockDragOutsidePreviews(): void {
		const block: Block = this.activity.activeBlocks()[0];
		this.dragOutsideService.clearBlock();
		if (block) this.setBlockDragCanvasVisibility(block, true);
	}

	private setBlockDragCanvasVisibility(block: Block, visible: boolean): void {
		const opacity: number = visible ? 1 : 0;
		const canvasBlock: Konva.Group = this.canvasDrawerService.getCanvasBlock(block);

		canvasBlock.opacity(opacity);
		block.products.forEach((product: ProductGroup) => {
			this.canvasDrawerService.getCanvasProduct(product).opacity(opacity);
		});

		this._planogramLayer.batchDraw();
		this._blocksLayer.batchDraw();
	}

	private updateBlockDragOutsidePreviews(block: Block): void {
		if (!this._mainStage || !this._canvasContainer?.nativeElement) return;

		this.dragOutsideService.updateBlock({
			items: [{
				key: block,
				canvasItem: this.canvasDrawerService.getCanvasBlockBody(block),
				products: block.products.map((product: ProductGroup) => ({
					key: product,
					canvasItem: this.canvasDrawerService.getCanvasProduct(product)
				}))
			}],
			stage: this._mainStage,
			container: this._canvasContainer.nativeElement,
			overCanvasTargets: Array.from(document.querySelectorAll('detailed-element-widget'))
				.filter((el) => el instanceof HTMLElement) as Array<HTMLElement>
		});

		this.setBlockDragCanvasVisibility(block, !this.dragOutsideService.hasBlockPreview(block));
	}

	private revertBlockDrag(block: Block, planogram: IPlanogram = this.planogram()): void {
		this.clearBlockDragOutsidePreviews();
		block.products.forEach((p: ProductGroup) => this.canvasDrawerService.getCanvasProduct(p).destroyChildren());
		const elementIds: Array<string> = block.elements.map((el: Element) => el.id);
		this.renderPlanogramLayer(elementIds);
		this.renderBlocksLayer(elementIds);
		this.activity.requestCanvasUpdate([planogram], elementIds, undefined, [PlanogramCanvasLayer.Planogram, PlanogramCanvasLayer.Blocks]);
		this.stopProductMove();
	}

	private getBlockProductPlanogramX(
		product: ProductGroup,
		block: Block,
		canvasBlock: Konva.Group,
		blockRect: Konva.Rect,
		blockPoint: Point,
		planogram: IPlanogram,
		sourcePlanogram?: IPlanogram
	): number {
		if (!sourcePlanogram || sourcePlanogram.stand === planogram.stand) {
			return Utils.round(
				(this.canvasDrawerService.getCanvasProduct(product).x() - canvasOffset - planogram.beginPoint.x) / planogram.scale
			);
		}

		const blockCanvasLeft: number = canvasBlock.x() + blockRect.x();
		const productCanvasLeft: number = this.canvasDrawerService.getCanvasProduct(product).x();
		const offsetX: number = Utils.round((productCanvasLeft - blockCanvasLeft) / sourcePlanogram.scale);

		return Utils.round(blockPoint.x + offsetX);
	}

	private showBlockChrome(block: Block): void {
		this.canvasDrawerService.getCanvasBlockBorders(block).show();
		this.canvasDrawerService.getCanvasBlockSizes(block).show();
		this.canvasDrawerService.getCanvasBlockTitle(block).show();
	}

	private getBlockDropPointFromDrag(
		planogram: IPlanogram,
		block: Block,
		canvasBlock: Konva.Group,
		blockRect: Konva.Rect
	): Point {
		const x: number = canvasBlock.x() + blockRect.x() - planogram.beginPoint.x - canvasOffset;
		const y: number = canvasBlock.y() + blockRect.y() - planogram.beginPoint.y - canvasOffset;

		return this.toBlockPlanogramPoint(planogram, block, this.canvasGetPoint(x, y));
	}

	private getBlockDropPointFromScreen(block: Block, planogram: IPlanogram): Point | null {
		const canvasPoint: Point = this.dragOutsideService.getBlockDropPoint(block, this._mainStage);
		if (!canvasPoint) return null;

		return this.toBlockPlanogramPoint(
			planogram,
			block,
			this.canvasGetPoint(
				canvasPoint.x - planogram.beginPoint.x - canvasOffset,
				canvasPoint.y - planogram.beginPoint.y - canvasOffset
			)
		);
	}

	private toBlockPlanogramPoint(planogram: IPlanogram, block: Block, point: Point): Point {
		if (this.flipMode()) {
			point.x = planogram.stand.Width - block.width - point.x;
		}

		return point;
	}

	private handleBlockDragEnd(
		block: Block,
		planogram: IPlanogram,
		canvasBlock: Konva.Group,
		blockRect: Konva.Rect
	): void {
		if (!this.activity.activeBlocks().length) {
			blockRect.off('dragend');
			this.stopProductMove();
			return;
		}

		const dropCanvases: Array<HTMLElement> = DragOutsideService.getDropTargetCanvases();
		const targetCanvas: HTMLElement = this.dragOutsideService.findBlockTargetCanvas(
			block,
			dropCanvases,
			this._canvas.nativeElement
		);

		this.showBlockChrome(block);

		if (targetCanvas !== this._canvas.nativeElement) {
			if (targetCanvas.dataset.hasOwnProperty('canvasDetailedElement')) {
				this.revertBlockDrag(block);
				return;
			}

			this.dragOutsideService.blockDrop$.next(targetCanvas);
			return;
		}

		if (!this.applyBlockDrop(
			planogram,
			block,
			this.getBlockDropPointFromDrag(planogram, block, canvasBlock, blockRect),
			canvasBlock,
			blockRect
		)) {
			this.revertBlockDrag(block);
		}
	}

	private resolveBlockProductElement(
		copy: Block,
		productsMatrix: Array<number>,
		index: number,
		x: number
	): Element | null {
		const matrixIndex: number = productsMatrix[index];
		let element: Element = matrixIndex >= 0 ? copy.extractPgElement(matrixIndex) : null;

		if (element?.canHaveSiblings && !element.shapes[0].isPointOn(x)) {
			element = copy.elements.find((el: Element) => el.shapes[0].isPointOn(x)) ?? element;
		}

		return element ?? copy.elements.find((el: Element) => el.shapes[0].isPointOn(x)) ?? null;
	}

	private applyBlockProductPosition(product: ProductGroup, element: Element): void {
		product.Element = element;

		switch (element.parent.type) {
			case EquipmentElements.Box:
			case EquipmentElements.SlicedBox:
				product.begin.y = element.getBoundingY().min;
				break;

			case EquipmentElements.Hook:
				product.begin.x = Utils.round(
					element.shapes[0].begin.x -
					(product.Width - (element.shapes[0] as Rectangle).width) / 2
				);
				product.begin.y = Utils.round(element.getBoundingY().min - product.Height - element.hangerDistance);
				break;

			default:
				product.begin.y = element.getBoundingY().max;
		}
	}

	private buildBlockProductPlacements(
		block: Block,
		copy: Block,
		point: Point,
		canvasBlock: Konva.Group,
		blockRect: Konva.Rect,
		planogram: IPlanogram,
		sourcePlanogram?: IPlanogram
	): {
		placements: Array<{ product: ProductGroup; x: number; element: Element }>;
		error?: string;
	} {
		block.products.sort((a, b) => a.begin.z - b.begin.z);
		const productsMatrix: Array<number> = !block.elements.length ? copy.productsMatrix : block.productsMatrix;
		const placements: Array<{ product: ProductGroup; x: number; element: Element }> = [];

		for (let index = 0; index < copy.products.length; index++) {
			const product: ProductGroup = copy.products[index];
			const x: number = this.getBlockProductPlanogramX(
				product, block, canvasBlock, blockRect, point, planogram, sourcePlanogram
			);
			const element: Element = this.resolveBlockProductElement(copy, productsMatrix, index, x);

			if (!element) {
				return { placements: [], error: getBlockProductPlacementErrorKey(null, product) };
			}

			const productCopy: ProductGroup = product.copy();
			productCopy.begin.x = x;
			productCopy.Element = element;

			const placementError: string | null = getBlockProductPlacementErrorKey(element, productCopy);
			if (placementError) {
				return { placements: [], error: placementError };
			}

			placements.push({ product, x, element });
		}

		return { placements };
	}

	private applyBlockDrop(
		planogram: IPlanogram,
		block: Block,
		point: Point,
		canvasBlock: Konva.Group,
		blockRect: Konva.Rect,
		sourcePlanogram?: IPlanogram
	): boolean {
		const isCrossPlanogram: boolean = !!sourcePlanogram && sourcePlanogram.stand !== planogram.stand;
		const scale: number = planogram.scale;
		const canvasScale: number = isCrossPlanogram ? sourcePlanogram.scale : scale;
		const otherBlocks: Array<Block> = planogram.stand.blocks.filter((i: Block) => i != block);
		const copy: Block = block.copy();

		if (isCrossPlanogram) {
			copy.setStand(planogram);
			copy.clearCache();
		}

		copy.updateCoordinates([Object.assign({}, point)]);

		const intersections = this.canDrawBlock(copy, otherBlocks);
		const paths: Array<Array<Point>> = [];

		if (intersections.length) {
			this.snackBarService.setSnackBarNotification({ messageText: 'BLOCK_OVER_BLOCKS_ERROR' });

			let count = 10;

			while (count) {
				const inner_count = 5;
				const inner_paths: Array<Point> = [];
				let copy_point = Object.assign({}, point);
				let copied_block = copy.copy();
				const choice = Utils.randomElementFromArray(BLOCK_CHOICES);

				inner_loop: for (let i = 0; i < inner_count; i++) {
					let innerIntersections = this.canDrawBlock(copied_block, otherBlocks);
					const block_bounding_x = copied_block.boundingX;
					const block_bounding_y = copied_block.boundingY;

					for (let j = 0; j < innerIntersections.length; j++) {
						const min_x = Math.min.apply(null, innerIntersections[j].getCoordinates().map((c) => c.x));
						const max_x = Math.max.apply(null, innerIntersections[j].getCoordinates().map((c) => c.x));
						const min_y = Math.min.apply(null, innerIntersections[j].getCoordinates().map((c) => c.y));
						const max_y = Math.max.apply(null, innerIntersections[j].getCoordinates().map((c) => c.y));

						if (i % 2 == choice) {
							if (block_bounding_x.min < min_x && block_bounding_x.max > min_x) {
								copy_point.x = min_x - copy.width;
							}
							if (block_bounding_x.min < max_x && block_bounding_x.min == min_x) {
								copy_point.x = max_x;
							}
						} else {
							if (block_bounding_y.min < min_y && block_bounding_y.max > min_y) {
								copy_point.y = min_y - copy.height;
							}
							if (block_bounding_y.min < max_y && block_bounding_y.min == min_y) {
								copy_point.y = max_y;
							}
						}

						inner_paths.push(Object.assign({}, copy_point));
						copied_block.updateCoordinates([Object.assign({}, copy_point)]);

						innerIntersections = this.canDrawBlock(copied_block, otherBlocks);

						if (!innerIntersections.length) break inner_loop;
					}
				}

				paths.push(inner_paths);
				count--;
			}

			if (!paths?.length) {
				return false;
			}

			const distances = paths
				.map((points) => points.slice(-1)[0])
				.map((p, idx) => [Utils.distance(point.x, point.y, p.x, p.y), idx]);
			const idx = distances.sort((a, b) => a[0] - b[0])[0][1];
			const new_p = paths[idx].slice(-1)[0];

			copy.products.forEach((p: ProductGroup) => {
				this.canvasDrawerService.getCanvasProduct(p).x(
					this.canvasDrawerService.getCanvasProduct(p).x() - (point.x - new_p.x) * canvasScale
				);
				this.canvasDrawerService.getCanvasProduct(p).y(
					this.canvasDrawerService.getCanvasProduct(p).y() - (point.y - new_p.y) * canvasScale
				);
			});

			point.x = new_p.x;
			point.y = new_p.y;
			copy.updateCoordinates([Object.assign({}, point)]);
		}

		const finalIntersections = this.canDrawBlock(copy, otherBlocks);

		if (finalIntersections.length) {
			return false;
		}

		if (block.products.length && !this.canMoveBlock(block, copy)) {
			this.snackBarService.setSnackBarNotification({
				messageText: 'BLOCK_ELEMENTS_ERROR',
				translateParams: {
					details: buildBlockElementsMismatchDetails(
						block,
						copy,
						(key, params) => this.translate.instant(key, params)
					)
				},
				durationSeconds: 5
			});
			return false;
		}

		const { placements, error: placementError } = this.buildBlockProductPlacements(
			block, copy, point, canvasBlock, blockRect, planogram, sourcePlanogram
		);

		if (placementError) {
			this.snackBarService.setSnackBarNotification({ messageText: placementError });
			return false;
		}

		let sourceNormalizeElements: Array<Element> = [];

		if (isCrossPlanogram) {
			sourceNormalizeElements = this.activity.removeBlockFromStandForTransfer(sourcePlanogram.stand, block);
			block.setStand(planogram);
			block.depth = planogram.stand.Depth;
			planogram.stand.blocks.push(block);

			if (block.products.length) {
				this.activity.ensureProductsInPlanogramCatalog(planogram, block.products);
				planogram.stand.products.push(...block.products);
			}
		}

		for (const { product, x, element } of placements) {
			product.begin.x = x;
			this.applyBlockProductPosition(product, element);

			if (!isCrossPlanogram) {
				const pIdx = planogram.stand.products.indexOf(product);
				planogram.stand.products.splice(pIdx, 1);
				planogram.stand.products.push(product);
			}
		}

		block.updateCoordinates([Object.assign({}, point)]);

		block.products
			.sort((a, b) => a.getBoundingY().min - b.getBoundingY().min)
			.forEach((pg: ProductGroup) => {
				if (pg.dependProduct && !block.products.includes(pg.dependProduct)) {
					pg.dependProduct = null;
				} else if (pg.dependProduct && block.products.includes(pg.dependProduct)) {
					pg.begin.x = pg.dependProduct.begin.x;
					pg.begin.y = pg.dependProduct.getBoundingY().max;
				}
			});

		const blockProductErrors = block.setProducts(
			Utils.to1DArray(planogram.stand.blocks.filter((b: Block) => block != b).map((b: Block) => b.products))
		);

		if (blockProductErrors.length) {
			this.activity.showDeleteMessage$.next({ block: block, errors: blockProductErrors });
		}

		this.activity.refreshActiveBlocks();
		const elements: Array<Element> = isCrossPlanogram ? copy.elements : block.elements;
		const normalized: Array<Element> = this.activity.normalizeElements(planogram.stand, [...elements, ...block.elements]);

		this.activity.setNumbersForPerforation$.next(normalized);

		const updatePlanograms: Array<IPlanogram> = isCrossPlanogram ? [sourcePlanogram, planogram] : [planogram];
		const updateElementIds: Array<string> = isCrossPlanogram
			? Utils.unique([...sourceNormalizeElements, ...normalized].map((el: Element) => el.id))
			: normalized.map((el: Element) => el.id);

		this.activity.setPlanogram(planogram);
		this.clearBlockDragOutsidePreviews();

		this.activity.requestCanvasUpdate(
			updatePlanograms,
			updateElementIds,
			undefined,
			isCrossPlanogram ? [PlanogramCanvasLayer.Planogram, PlanogramCanvasLayer.Blocks] : undefined
		).subscribe({
			complete: () => this.stopProductMove()
		});

		if (isCrossPlanogram) {
			this.activity.historyGenerate$.next(sourcePlanogram.main_stand);
			this.activity.historyGenerate$.next(planogram.main_stand);
			this.activity.setFocusedCanvas(planogram.main_stand, null, this._canvas.nativeElement);
		} else {
			this.activity.historyGenerate$.next();
		}

		return true;
	}

	private handleBlockOutsideDrop(): void {
		const block: Block = this.activity.activeBlocks()[0];
		if (!block) return;

		const sourcePlanogram: IPlanogram = this.activity.getPlanogramByBlock(block);
		const targetPlanogram: IPlanogram = this.planogram();
		if (!sourcePlanogram || sourcePlanogram.stand === targetPlanogram.stand) return;

		const point: Point = this.getBlockDropPointFromScreen(block, targetPlanogram);
		if (!point) {
			this.revertBlockDrag(block, sourcePlanogram);
			return;
		}

		const canvasBlock: Konva.Group = this.canvasDrawerService.getCanvasBlock(block);
		const blockRect: Konva.Rect = this.canvasDrawerService.getCanvasBlockBody(block);

		this.showBlockChrome(block);

		if (!this.applyBlockDrop(targetPlanogram, block, point, canvasBlock, blockRect, sourcePlanogram)) {
			this.revertBlockDrag(block, sourcePlanogram);
		}
	}

	private handlePOSMaterialOutsideDrop(): void {
		const materials: Array<POSMaterial> = [...this.activity.activePosMaterials()];
		if (!materials.length) return;

		const sourcePlanogram: IPlanogram = this.activity.getPlanogramByPOSMaterial(materials[0]);
		materials.forEach((material: POSMaterial) => {
			const canvasPoint: Point = this.dragOutsideService.getPosDropPoint(material, this._mainStage);
			if (canvasPoint) {
				material.x = Utils.round((canvasPoint.x - canvasOffset - this.planogram().beginPoint.x) / this.planogram().scale);
				material.y = Utils.round((canvasPoint.y - canvasOffset - this.planogram().beginPoint.y) / this.planogram().scale);
			}
		});

        this.activity.removePOSMaterialsFromStand(sourcePlanogram.stand, materials);
		this.planogram().stand.POSMaterials.push(...materials);
		this.activity.setPlanogram(this.planogram());

		this.clearPosDragOutsidePreviews();
		this.activity.requestCanvasUpdate([sourcePlanogram, this.planogram()], [], undefined, [PlanogramCanvasLayer.PosMaterials]);

		this.activity.historyGenerate$.next(sourcePlanogram.main_stand);
		this.activity.historyGenerate$.next(this.planogram().main_stand);
		this.activity.setPOSMaterialActive(materials[0]);
	}

}
